/* eslint-disable no-irregular-whitespace */
/* eslint-disable no-shadow */
/* eslint-disable prefer-object-spread */
import { createSlice } from '@reduxjs/toolkit';
import axiosInstance, { CODICE_TASK_NON_IN_CARICO, MESSAGGIO_TASK_NON_IN_CARICO } from '../../../data/axiosInstance/axiosInstance';
import executor from '../../../data/axiosInstance/executor';
import { EnabledDisabledStateTrueFalsePresaCarico, DisabledStateTrueFalsePresaCarico } from '../../customizer/CustomizerSlice';
import { removeTaskToNav } from '../addNavTask/AddNavTaskSlice';
import { fetchNote } from '../diarioTrattazione/NoteSlice';
import { checkTotalItemMessageToast, setTotalItemMessageToast } from '../ws/WsSlice';
import profileValidation from '../../../components/jwt/profile.validation';
import EnumService from '../../../components/jwt/enum.service';
import { istanteUtc } from '../../../utils/dataOraRoma';
import { setSelectedMultivaluta, setSelectedMultivalutaIncarico } from '../multiValuta/MultiValutaSlice';
import {
  normalizeFiduciariConfig,
  normalizeRicercaFiduciariResponse,
  safeString,
} from '../../../components/detailTask/taskIncarico/fiduciari.mapper';
import { normalizeTaskListForChatFlag } from '../../../components/hooks/gridHooks/chatFlag.utils';
import { idTaskInCarico, isTaskInCarico } from './taskInCarico';
import { normalizeTaskListPriority } from '../../../components/hooks/gridHooks/priority.utils';
import {
  SEARCH_CONTEXT,
  FIDUCIARI_QUERY_PARAM,
  FIDUCIARI_SEARCH_ERROR,
  resolveSearchContext,
} from '../../../components/detailTask/taskIncarico/fiduciari.models';
import { AREA_GEOGRAFICA_ALLOWED } from '../../../components/detailTask/taskIncarico/filters.utils';

const API_URL = `${process.env.REACT_APP_API_SINISTRI_URL}`;
const API_URL_WORKFLOW = `${process.env.REACT_APP_API_WORKFLOW_URL}`;
const API_URL_DMS = `${process.env.REACT_APP_API_DMS_URL}`;

const updateTicketIfFound = (state, updateFn) => {
  const taskId = state.openedTicket?.datiTask?.idTask;
  if (!taskId) return;

  const index = state.allOpenedTicket.findIndex(
    // eslint-disable-next-line eqeqeq
    (taskItem) => taskItem.datiTask.idTask == taskId // Using loose equality as in original code
  );

  if (index !== -1) {
    updateFn(state, index);
  }
};

const taskIdKey = (task) => {
  if (task?.id == null) return '';
  const raw = String(task.id);
  const numeric = raw.match(/\d+/);
  return numeric ? numeric[0] : raw;
};

const mergeUnreadTaskIntoBase = (baseTask = {}, unreadTask = {}) => {
  const merged = { ...baseTask, ...unreadTask };

  if (baseTask?.id != null && baseTask.id !== '') {
    merged.id = baseTask.id;
  }
  if (baseTask?.ticket_code != null && baseTask.ticket_code !== '') {
    merged.ticket_code = baseTask.ticket_code;
  }

  return merged;
};

/**
 * Normalizzazione delle righe griglia: flag chat + priorita' uniformata.
 * Unico punto per non dover ricordare, ogni volta che si popola una lista,
 * quali normalizzazioni servono.
 */
const normalizeGridTaskList = (tasks) => normalizeTaskListPriority(normalizeTaskListForChatFlag(tasks));

const mergeTasksById = (baseTasks = [], incomingTasks = [], appendMissing = false) => {
  const normalizedIncoming = normalizeTaskListForChatFlag(incomingTasks) || [];
  if (!Array.isArray(baseTasks) || normalizedIncoming.length === 0) {
    return baseTasks;
  }

  const incomingById = new Map(
    normalizedIncoming
      .map((task) => [taskIdKey(task), task])
      .filter(([id]) => id)
  );
  const seenIds = new Set();
  const merged = baseTasks.map((task) => {
    const id = taskIdKey(task);
    if (!id || !incomingById.has(id)) {
      return task;
    }
    seenIds.add(id);
    return mergeUnreadTaskIntoBase(task, incomingById.get(id));
  });

  if (!appendMissing) {
    return merged;
  }

  normalizedIncoming.forEach((task) => {
    const id = taskIdKey(task);
    if (id && !seenIds.has(id)) {
      merged.push(task);
      seenIds.add(id);
    }
  });
  return merged;
};

const initialState = {
  ticketsLavorabili: [],//Tutti i ticket visualizzati nella gridLavorabili (default fetch)
  // OSSQ-2381: insieme COMPLETO dei task lavorabili (endpoint non paginato,
  // lo stesso che alimentava ticketsLavorabili prima della paginazione).
  // Serve a chi deve ragionare su TUTTI i task e non sulla pagina a video:
  // inventario riserve, valori dei filtri di colonna, ricerca duplicati.
  // Caricato on-demand da quei punti, non al mount della griglia.
  ticketsLavorabiliCompleti: [],
  lavorabiliCompletiLoaded: false,
  lavorabiliCompletiLoading: false,
  paginationLavorabili: { page: 0, size: 50, totalElements: 0, totalPages: 0 },
  // Ricerca ibrida (spec §4.3 + §5): risultato paginato server-side con filtri.
  // Sostituisce ticketsLavorabiliFull (deprecato: full-scan client-side annullava
  // i benefici della paginazione).
  searchedTickets: [],
  searchPagination: { page: 0, size: 50, totalElements: 0, totalPages: 0 },
  searchFilters: {},
  searchLoading: false,
  ticketsAssegnate: [],//Tutti i ticket visualizzati nella gridAssegnate
  ticketsScadenziario: [], //Tutti i ticket visualizzati nella gridScadenziario
  ticketsScarti: [], //Pagamenti scartati da Piteco visualizzati nel sezionale scarti
  // Lazy-load per tab (WS-1): stato di caricamento per lista, per evitare di
  // scaricare tutte le liste al mount. La griglia lavorabili resta paginata
  // server-side (fetchTicketsLavorabili sempre "fresca"); questi flag servono
  // solo alla prima apertura del tab e all'invalidazione da Aggiorna/Pulisci.
  // CSP-3543: revisioneSap e' la lista della tab 9 "Pagamenti da revisionare SAP".
  listsLoaded: { lavorabili: false, assegnate: false, scadenziario: false, scarti: false, revisioneSap: false },
  listsLoading: { lavorabili: false, assegnate: false, scadenziario: false, scarti: false, revisioneSap: false },
  // CSP-3428: "caricata" e "ancora valida" sono due cose diverse. Il flag
  // listsLoaded vive in Redux e sopravvive alla navigazione, quindi dopo aver
  // lavorato un task il rientro in griglia non ricaricava nulla. listsDirty
  // segna che qualcosa e' cambiato e la lista va riletta alla prossima
  // apertura del tab; listsFetchedAt e' il momento dell'ultima lettura andata
  // a buon fine, usato per il TTL di sicurezza. Su sprint-50 le liste includono
  // anche il sezionale scarti, che quindi segue le stesse regole.
  listsDirty: { lavorabili: false, assegnate: false, scadenziario: false, scarti: false, revisioneSap: false },
  listsFetchedAt: { lavorabili: 0, assegnate: 0, scadenziario: 0, scarti: 0, revisioneSap: 0 },
  ticketsRevisioneSap: [], //CSP-3543: pagamenti con esito SAP D/R da revisionare (tab 9)
  currentFilter: 'total_tickets',//To do gestione filtri grid
  ticketSearch: '',//To do Gestione Filtri
  openedTicket: {},//
  //setAdminData: {},
  allOpenedTicket: [],
  totalOpenedTicket: 0,
  taskId: '',
  loading: false,
  loadingGrid: false,
  isSlowFormVisible: false,
  taskIdInCharge: '',
  userData: {},
  calcoloMassimaliExtOpnenedticket: {},//Per Modale garanzie Massimali 
  recuperoFranchigia: [],
  totaleRecuperoFranchigia: 0,
  listaBeneficiariRecuperoFranchigia: [],
  isButtonEnabledMassimaleGaranzieVisible: true,
  listaBeneficiariRecuperoFranchigiaTable: [],
  listaDebitoreRecuperoRivalsa: [],
  // configurazione dinamica tipi incarico / competenze / specializzazioni
  configTipiIncarico: null,
  fiduciariConfig: {
    tipiFiduciario: [],
    tipiIncarico: [],
  },
  areaGeograficaOptions: {
    nazioni: [],
    regioni: [],
    province: [],
  },
  areaGeograficaLoading: {
    nazioni: false,
    regioni: false,
    province: false,
  },
  language: {},
  dataNotifica: null,
  setEmailLanguage: null,
  checkWorld: {
    checked: false,
    status: '',
    gridValues: [],
    dataEsecuzione: null
  },
  verificaAmministrativaBene: {
    motivazione: null,
    isAlertInsertVisible: false,
    isAlertDetailVisible: false,
    alertNoDataEvntoDataRis: false
  },
  motivazioneSenzaSeguito: '',
  motiviSenzaSeguito: [],
  // CSP-3170: catena delle clonazioni del sinistro corrente (sinistri origine risalendo).
  catenaClonazioni: [],
  // CSP-2423: reason code of the "senza seguito" closure and estimated damages entirely in deductible
  codiceMotivazioneSenzaSeguito: '',
  dannoFranchigiaSenzaSeguito: null,
  motivazioniAnnullamentoIncarico: [],
  motivazioniAnnullamentoIncaricoLoading: false,
  motivazioneAnnullamentoIncaricoId: '',
  anticipoLiquidazione: {
    statoRichiestaAnticipo: null,
    popupLiquidazione: false,
  },
  popupFattura: {
    open: false,
    type: 'success',
    message: '',
  },
  emailPayloadRispostaForEntity: {},
  emailTipologie: [],
  diniego: {},
  sepaCountries: null,
  selectedGaranzieIngresso: [],
  // cached suggested dates per pagamentoId: { [pagamentoId]: { dataSuggerita, fonte, fetchedAt } }
  suggestedDates: {},
};

export const TicketSlice = createSlice({
  name: 'ticket',
  initialState,
  reducers: {
    setTicketsLavorabiliCompleti: (state, action) => {
      state.ticketsLavorabiliCompleti = normalizeGridTaskList(action.payload) || [];
      state.lavorabiliCompletiLoaded = true;
    },
    setLavorabiliCompletiLoading: (state, action) => {
      state.lavorabiliCompletiLoading = !!action.payload;
    },
    // Invalida senza svuotare: i consumatori continuano a mostrare i dati
    // correnti finche' non arriva il refetch, evitando lo sfarfallio.
    invalidateLavorabiliCompleti: (state) => {
      state.lavorabiliCompletiLoaded = false;
    },
    getTicketsLavorabili: (state, action) => {
      //state.ticketsLavorabili = {};
      if (action.payload)
        state.ticketsLavorabili = normalizeGridTaskList(action.payload);
    },
    setPaginationLavorabili: (state, action) => {
      if (action.payload) {
        state.paginationLavorabili = {
          page: action.payload.page ?? 0,
          size: action.payload.size ?? 50,
          totalElements: action.payload.totalElements ?? 0,
          totalPages: action.payload.totalPages ?? 0,
        };
      }
    },
    // Reducers ricerca ibrida (spec §5). Sostituiscono la coppia
    // ticketsLavorabiliFull / ticketsLavorabiliFullLoadedAt (deprecata).
    setSearchedTickets: (state, action) => {
      if (action.payload) {
        state.searchedTickets = normalizeTaskListForChatFlag(action.payload);
      } else {
        state.searchedTickets = [];
      }
    },
    setSearchPagination: (state, action) => {
      if (action.payload) {
        state.searchPagination = {
          page: action.payload.page ?? 0,
          size: action.payload.size ?? 50,
          totalElements: action.payload.totalElements ?? 0,
          totalPages: action.payload.totalPages ?? 0,
        };
      }
    },
    setSearchFilters: (state, action) => {
      state.searchFilters = action.payload || {};
    },
    setSearchLoading: (state, action) => {
      state.searchLoading = !!action.payload;
    },
    resetSearch: (state) => {
      state.searchedTickets = [];
      state.searchPagination = { page: 0, size: 50, totalElements: 0, totalPages: 0 };
      state.searchFilters = {};
      state.searchLoading = false;
    },
    // DEPRECATED — vecchio full-scan cliente-side. Il reducer resta per
    // retrocompatibilità durante il rollout; verrà rimosso in fase 3
    // insieme al thunk fetchTicketsLavorabiliFull.
    getTicketsLavorabiliFull: (state, action) => {
      if (action.payload) {
        state.searchedTickets = normalizeTaskListForChatFlag(action.payload);
      }
    },
    getTicketsScadenziario: (state, action) => {
      if (action.payload)
        state.ticketsScadenziario = normalizeTaskListForChatFlag(action.payload);
    },
    getTicketsScarti: (state, action) => {
      state.ticketsScarti = action.payload
        ? normalizeTaskListForChatFlag(action.payload)
        : [];
    },
    // CSP-3543: righe di pagamento, non task: niente normalizzazione del flag chat
    getTicketsRevisioneSap: (state, action) => {
      state.ticketsRevisioneSap = Array.isArray(action.payload) ? action.payload : [];
    },
    getTicketsAssegnate: (state, action) => {
      // state.ticketsAssegnate = {};
      if (action.payload)
        state.ticketsAssegnate = normalizeTaskListForChatFlag(action.payload);
    },
    getTicketsChatUnread: (state, action) => {
      if (!action.payload) return;
      state.ticketsScadenziario = mergeTasksById(state.ticketsScadenziario, action.payload, true);
      state.ticketsLavorabili = mergeTasksById(state.ticketsLavorabili, action.payload, false);
      state.ticketsAssegnate = mergeTasksById(state.ticketsAssegnate, action.payload, false);
    },
    getTicket: (state, action) => {
      const currentTaskId = state.openedTicket?.datiTask?.idTask;
      const incomingTaskId = action.payload?.datiTask?.idTask;
      const selectedGaranzieIngresso = currentTaskId && incomingTaskId && currentTaskId === incomingTaskId
        ? state.openedTicket?.selectedGaranzieIngresso || []
        : [];
      state.openedTicket = {};
      state.openedTicket = { ...{}, ...action.payload, selectedGaranzieIngresso };
    },
    getTicketTree: (state, action) => {
      // eslint-disable-next-line eqeqeq
      const index = state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask === action.payload.datiTask.idTask);
      if (index !== -1) {
        state.allOpenedTicket[index].datiTaskFigli = action.payload?.datiTaskFigli;
      }
      // `openedTicket` e' una COPIA di allOpenedTicket[index] (Object.assign in getTicket e
      // addTaskToNav), non un riferimento: senza questa riga l'albero laterale, che legge
      // openedTicket.datiTaskFigli, resta fermo. Non si notava perche' gli altri chiamanti
      // navigano subito dopo e la fetchTicket ricostruisce tutto [CSP-3310].
      if (String(state.openedTicket?.datiTask?.idTask) === String(action.payload?.datiTask?.idTask)) {
        state.openedTicket = { ...state.openedTicket, datiTaskFigli: action.payload?.datiTaskFigli };
      }
    },
    setVisibilityFilter: (state, action) => {//NOTUSED
      state.currentFilter = action.payload;
    },
    SearchTicket: (state, action) => {//NOTUSED
      state.ticketSearch = action.payload;
    },
    DeleteTicket: (state, action) => {//NOTUSED
      const index = state.tickets.findIndex((ticket) => ticket.Id === action.payload);
      state.tickets.splice(index, 1);
    },
    setOpenedTicketClaimData: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        // Preserve titoloAmministrativoId and idBene BEFORE overwriting datiDenuncia.
        // formData in ClaimData is initialised once on mount, so stale null values from formData
        // spread can clobber fields set later (e.g. idBene after bene selection). Explicit clears
        // (dataEvento change) dispatch setIdBeneAssicuratoAmministrativo(null) separately, so
        // preserving here is always safe and does not block intentional resets.
        const prevTitoloAmministrativoId = state.openedTicket?.datiDenuncia?.titoloAmministrativoId;
        const prevIdBene = state.openedTicket?.datiDenuncia?.idBene;
        state.openedTicket.datiDenuncia = { ...action.payload };
        state.allOpenedTicket[index].datiDenuncia = { ...action.payload };
        state.openedTicket.datiDenuncia.titoloAmministrativoId = prevTitoloAmministrativoId;
        state.allOpenedTicket[index].datiDenuncia.titoloAmministrativoId = prevTitoloAmministrativoId;
        state.openedTicket.datiDenuncia.idBene = prevIdBene;
        state.allOpenedTicket[index].datiDenuncia.idBene = prevIdBene;
      });
    },
    setOpenedTicketDatiCausa: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiCausa);
        state.allOpenedTicket[index].datiCausa = { ...tckObject };
        state.allOpenedTicket[index].datiCausa = { ...action.payload };
      });
    },
    setOpenedTicketDatiIncarico: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiIncarico);
        state.openedTicket.datiIncarico = { ...tckObject, ...action.payload };
        state.allOpenedTicket[index].datiIncarico = { ...tckObject, ...action.payload };
      });
    },
    setOpenedTicketAdminData: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiDenuncia.titoloAmministrativoId = action.payload;
        //dopo aver reperito il ticket dallo store, lo popoliamo con i dati denuncia
        const tckObject = Object.assign({}, state.openedTicket?.datiDenuncia);
        state.allOpenedTicket[index].datiDenuncia = { ...tckObject };
        state.allOpenedTicket[index].datiDenuncia.titoloAmministrativoId = action.payload;
      });
    },
    setOpenedTicketAdminDataRender: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.adminDataRender = action.payload;
        state.allOpenedTicket[index].adminDataRender = action.payload;
      });
    },
    setOpenedTicketNPolizza: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiTask.nPolizza = action.payload;
        state.allOpenedTicket[index].datiTask.nPolizza = action.payload;
      });
    },
    clearDatiDenuncia: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiDenuncia.numeroPolizza = action.payload;
        state.allOpenedTicket[index].datiDenuncia.numeroPolizza = action.payload;
        state.openedTicket.datiDenuncia.titoloAmministrativoId = action.payload;
        state.allOpenedTicket[index].datiDenuncia.titoloAmministrativoId = action.payload;
        state.openedTicket.datiDenuncia.idBene = action.payload;
        state.allOpenedTicket[index].datiDenuncia.idBene = action.payload;
        state.openedTicket.datiDenuncia.motivazione = action.payload;
        state.allOpenedTicket[index].datiDenuncia.motivazione = action.payload;
      });
    },
    setDenunciaMotivazione: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiDenuncia.motivazione = action.payload;
        state.openedTicket.datiDenuncia.motivazione = action.payload; //this is shit, we will correct on alert
      });
    },
    setMotivazioniAnnullamentoIncarico: (state, action) => {
      state.motivazioniAnnullamentoIncarico = action.payload || [];
    },
    setMotivazioniAnnullamentoIncaricoLoading: (state, action) => {
      state.motivazioniAnnullamentoIncaricoLoading = action.payload;
    },
    setMotivazioneAnnullamentoIncaricoId: (state, action) => {
      state.motivazioneAnnullamentoIncaricoId = action.payload;
    },
    setTipoIncaricato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.tipoIncaricato);
        state.allOpenedTicket[index].tipoIncaricato = { ...tckObject };
        state.allOpenedTicket[index].tipoIncaricato = action.payload;
        const isPreMigration = state.allOpenedTicket[index]?.datiIncarico?.preMigration === true;
        if (isPreMigration) {
          state.allOpenedTicket[index].datiIncarico = { ...state.allOpenedTicket[index].datiIncarico, tipoIncarico: action.payload };
        }
      });
    },

    setIdTicketInCharge: (state, action) => {
      if (!isTaskInCarico(state.taskIdInCharge, action.payload)) {  //se il ticket non è già in carico aggiungilo alla lista
        const oldTaskIdInCharge = state.taskIdInCharge;
        state.taskIdInCharge = '';
        state.taskIdInCharge = oldTaskIdInCharge.concat(action.payload, ";");
      }
    },
    removeIdTicketInCharge: (state, action) => {
      state.taskIdInCharge = idTaskInCarico(state.taskIdInCharge)
        .filter((id) => id !== String(action.payload))
        .map((id) => `${id};`)
        .join('');
    },
    addOpenedTicket: (state, action) => {
      // eslint-disable-next-line eqeqeq
      if (state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask === action.payload.datiTask.idTask) === -1) {
        state.allOpenedTicket = [...state.allOpenedTicket, action.payload];
        state.totalOpenedTicket += 1;
      }
      state.taskId = {};
      state.taskId = action.payload.datiTask.idTask;  //aka getIdTicket
      state.openedTicket = {};
      state.openedTicket = Object.assign({}, action.payload, { selectedGaranzieIngresso: action.payload?.selectedGaranzieIngresso || [] });
      localStorage.setItem('idFlusso', action.payload?.datiTask?.idFlusso);
    },
    setOpenedTicketDatiTask: (state, action) => {
      // Aggiorna SOLO datiTask del task corrente, senza ricostruire openedTicket: i nodi
      // caricati a parte (es. listaDanneggiati) devono sopravvivere (CSP-2964).
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = { ...state.allOpenedTicket[index], datiTask: action.payload };
        state.openedTicket = { ...state.openedTicket, datiTask: action.payload };
      });
    },
    setOpenedTicketChangePolizza: (state, action) => {
      // eslint-disable-next-line eqeqeq
      const index = state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask === action.payload.datiTask.idTask);
      if (index !== -1) {
        state.allOpenedTicket.splice(index, 1);
        state.allOpenedTicket = [...state.allOpenedTicket, action.payload];
      }
    },
    addTaskToNavSinstro: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].navTaskListSinistro = state.allOpenedTicket[index].navTaskListSinistro !== undefined ? [...state.allOpenedTicket[index].navTaskListSinistro] : [];
        state.openedTicket.navTaskListSinistro = state.openedTicket.navTaskListSinistro !== undefined ? [...state.openedTicket.navTaskListSinistro] : [];
        if (state.openedTicket?.navTaskListSinistro) {
          state.allOpenedTicket[index].navTaskListSinistro = [...state.allOpenedTicket[index].navTaskListSinistro, action.payload];
          state.openedTicket.navTaskListSinistro = [...state.openedTicket.navTaskListSinistro, action.payload];
        }
      });
    },
    addOpenedTicketCached: (state, action) => {
      // eslint-disable-next-line eqeqeq
      const tckObject = { ...state.allOpenedTicket[(state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask == action.payload.datiTask.idTask))] };
      state.taskId = {};
      state.taskId = tckObject.datiTask.idTask;  //aka getIdTicket
      state.openedTicket = {};
      state.openedTicket = Object.assign({}, tckObject);
    },
    removeOpenedTicket: (state, action) => {
      // eslint-disable-next-line eqeqeq
      const index = state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask == action.payload.taskId);
      state.allOpenedTicket.splice(index, 1);
      state.totalOpenedTicket -= 1;
      state.taskId = {};
      state.openedTicket = {};
      state.taskId = (state.totalOpenedTicket > 0) ? state.allOpenedTicket[state.totalOpenedTicket - 1].datiTask.idTask : '0';
      state.openedTicket = (state.totalOpenedTicket > 0) ? Object.assign({}, state.allOpenedTicket[state.totalOpenedTicket - 1]) : {};
    },
    setModifiedTicket: (state, action) => {
      // eslint-disable-next-line eqeqeq
      const index = state.allOpenedTicket.findIndex((taskItem) => taskItem.datiTask.idTask == action.payload.taskId);
      state.allOpenedTicket[index] = Object.assign({}, action.modTicket);
    },
    resetModifiedTicket: (state) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = Object.assign({}, state.openedTicket);
      });
    },
    toogleLoading: (state, action) => {
      state.loading = action.payload;
    },
    toogleLoadingGrid: (state, action) => {
      state.loadingGrid = action.payload;
    },
    // Lazy-load per tab (WS-1)
    setListLoading: (state, action) => {
      const { key, value } = action.payload || {};
      if (key && key in state.listsLoading) state.listsLoading[key] = value;
    },
    setListLoaded: (state, action) => {
      const { key, value } = action.payload || {};
      if (key && key in state.listsLoaded) {
        state.listsLoaded[key] = value;
        // CSP-3428: una lettura andata a buon fine chiude il debito. Punto
        // unico: tutte le thunk di lista passano di qui dopo il fetch.
        if (value) {
          state.listsDirty[key] = false;
          state.listsFetchedAt[key] = Date.now();
        }
      }
    },
    resetTicketListsLoaded: (state) => {
      Object.keys(state.listsLoaded).forEach((k) => { state.listsLoaded[k] = false; });
    },
    // CSP-3428: un'operazione andata a buon fine puo' aver cambiato l'insieme
    // dei task. Non si ricarica subito (l'utente magari e' dentro il dettaglio):
    // si segna il debito e lo paga la prossima apertura del tab.
    markTicketListsDirty: (state) => {
      Object.keys(state.listsDirty).forEach((k) => { state.listsDirty[k] = true; });
    },
    setIncaricatiSearchLoading: (state, action) => {
      state.incaricatiSearchLoading = action.payload;
    },
    setIncaricatiSearchError: (state, action) => {
      state.incaricatiSearchError = action.payload;
    },
    toggleIsSlowFormVisible: (state, action) => {
      state.isSlowFormVisible = action.payload;
    },
    getListaDanneggiati: (state, action) => {
      state.openedTicket.listaDanneggiati = action.payload;
    },
    getListaBeneficiarioLqInd: (state, action) => {
      state.openedTicket.listaBeneficiarioLqInd = action.payload
    },
    getListaSoggettiCause: (state, action) => {
      state.openedTicket.datiCausa.listaSoggetti = action.payload;
    },
    setListaSoggettiCausa: (state, action) => {
      state.openedTicket.datiCausa.listaSoggetti = action.payload;
    },
    getRamiSinistro: (state, action) => {
      state.openedTicket.listaRamiSinistro = action.payload;
    },
    VisibleAlertDangerMora: (state, action) => {
      state.openedTicket.alertDangerVisible = action.payload;
    },
    VisibleAlertWarningMora: (state, action) => {
      state.openedTicket.alertWarningVisible = action.payload;
    },
    VisibleAlertNoDataEvDataRis: (state, action) => {
      state.openedTicket.alertNoDataEvntoDataRis = action.payload;
    },
    setTitoloIsInCopertura: (state, action) => {
      state.openedTicket.isInCopertura = action.payload;
    },
    getGaranzieDanneggiato: (state, action) => {
      state.openedTicket.listaGaranzieDanneggiato = action.payload;
    },
    setRamoGaranzia: (state, action) => {
      state.openedTicket.ramoGaranziaPut = action.payload;
    },
    setLanguageTemplateEmail: (state, action) => {
      state.setEmailLanguage = action.payload
    },
    setRamoGaranziaDanneggiato: (state, action) => {
      state.openedTicket.ramoGaranziaDanneggiato = action.payload;
    },
    /** CSP-3111: flag "pronta liquidazione" dell'incarico manuale, sincronizzato sui due rami del ticket. */
    setProntaLiquidazione: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiIncarico = {
          ...state.openedTicket?.datiIncarico,
          prontaLiquidazione: action.payload,
        };
        state.allOpenedTicket[index].datiIncarico = {
          ...state.allOpenedTicket[index].datiIncarico,
          prontaLiquidazione: action.payload,
        };
      });
    },
    setDanneggiatoId: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiIncarico = {
          ...state.openedTicket?.datiIncarico,
          danneggiatoId: action.payload,
        };
        state.allOpenedTicket[index].datiIncarico = {
          ...state.allOpenedTicket[index].datiIncarico,
          danneggiatoId: action.payload
        };
      });
    },
    getPrecedentiSinistri: (state, action) => {
      state.openedTicket.precedentiSinistri = action.payload;
    },
    getRiserveComplessive: (state, action) => {
      state.openedTicket.riserveComplessive = action.payload;
    },
    getCauseIstruttoria: (state, action) => {
      if (state.openedTicket?.datiTask?.idTask) {
        updateTicketIfFound(state, (state, index) => {
          const tckObject = Object.assign({}, state.openedTicket?.causeIstruttoria);
          state.allOpenedTicket[index].causeIstruttoria = { ...tckObject };
          state.allOpenedTicket[index].causeIstruttoria = action.payload;
        });
      }
    },
    getIncarichiIstruttoria: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.incarichiIstruttoria);
        state.allOpenedTicket[index].incarichiIstruttoria = { ...tckObject };
        state.openedTicket.incarichiIstruttoria = { ...tckObject };
        state.openedTicket.incarichiIstruttoria = action.payload;
        state.allOpenedTicket[index].incarichiIstruttoria = action.payload;
      });
    },
    getValidazioniIstruttoria: (state, action) => {
      state.openedTicket.validazioniIstruttoria = action.payload;
    },
    // configura dinamica tipi incarico / competenze / specializzazioni
    setConfigTipiIncarico: (state, action) => {
      const normalized = normalizeFiduciariConfig(action.payload);
      state.fiduciariConfig = normalized;
      // compat alias: kept for old consumers
      state.configTipiIncarico = { content: normalized.tipiFiduciario };
    },
    setFiduciariConfig: (state, action) => {
      const normalized = normalizeFiduciariConfig(action.payload);
      state.fiduciariConfig = normalized;
      state.configTipiIncarico = { content: normalized.tipiFiduciario };
    },
    setAreaGeograficaOptions: (state, action) => {
      const { key, items } = action.payload || {};
      if (!key || !Object.prototype.hasOwnProperty.call(state.areaGeograficaOptions, key)) return;
      state.areaGeograficaOptions[key] = items || [];
    },
    setAreaGeograficaLoading: (state, action) => {
      const { key, loading } = action.payload || {};
      if (!key || !Object.prototype.hasOwnProperty.call(state.areaGeograficaLoading, key)) return;
      state.areaGeograficaLoading[key] = loading;
    },
    addIncaricato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.incaricato);
        state.allOpenedTicket[index].incaricato = { ...tckObject };
        state.allOpenedTicket[index].incaricato = action.payload;
      });
    },
    /**
     * Azzera l'incaricato nel datiIncarico del ticket corrente.
     * Inizializza datiIncarico a {} se non presente (es. ticket ripreso dall'API
     * senza datiIncarico popolato) per evitare crash al momento del click su
     * Portal SI/NO in DatiIncarico.js.
     */
    removeIncaricato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        if (!state.allOpenedTicket[index].datiIncarico) {
          state.allOpenedTicket[index].datiIncarico = {};
        }
        const tckObject = Object.assign({}, state.openedTicket?.datiIncarico?.incaricato);
        state.allOpenedTicket[index].datiIncarico.incaricato = { ...tckObject };
        state.allOpenedTicket[index].datiIncarico.incaricato = action.payload;
      });
    },
    showAlertIncaricato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.alertIncaricato);
        state.allOpenedTicket[index].alertIncaricato = { ...tckObject };
        state.allOpenedTicket[index].alertIncaricato = action.payload;
      });
    },
    getListaIncaricatiInQuadro: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.listaIncaricatiInQuadro);
        state.allOpenedTicket[index].listaIncaricatiInQuadro = { ...tckObject };
        state.allOpenedTicket[index].listaIncaricatiInQuadro = action.payload;
      });
    },

    setTipologiaIncaricato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiIncarico);
        state.allOpenedTicket[index].datiIncarico = { ...tckObject };
        state.allOpenedTicket[index].datiIncarico.tipologia = action.payload;
      });
    },

    setDataNotificaRedux: (state, action) => {
      state.dataNotifica = action.payload;
    },

    getListaIncaricatoNoQuadro: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const normalized = Array.isArray(action.payload)
          ? action.payload
          : (action.payload ? [action.payload] : []);
        state.allOpenedTicket[index].listaIncaricatoNoQuadro = normalized;
      });
    },
    setIdCauseCollegate: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.idsCauseCollegate);
        state.allOpenedTicket[index].idsCauseCollegate = { ...tckObject };
        state.allOpenedTicket[index].idsCauseCollegate = action.payload;
      });
    },
    getRiservaSpeseIncarico: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.allOpenedTicket[index]);
        state.allOpenedTicket[index] = { ...tckObject, riservaSpeseIncarico: action.payload };
      });
    },
    setMotivazioneIncarico: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.motivazioneIncarico);
        state.allOpenedTicket[index].motivazioneIncarico = { ...tckObject };
        state.allOpenedTicket[index].motivazioneIncarico = action.payload;
      });
    },

    setMotivazioneIncaricoNoQuadro: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.motivazioneIncaricoNoQuadro);
        state.allOpenedTicket[index].motivazioneIncaricoNoQuadro = { ...tckObject };
        state.allOpenedTicket[index].motivazioneIncaricoNoQuadro = action.payload;
      });
    },
    setNotaRiserveAuthority: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.notaRiserveAuthority);
        state.allOpenedTicket[index].notaRiserveAuthority = { ...tckObject };
        state.allOpenedTicket[index].notaRiserveAuthority = action.payload;
      });
    },
    setNotaRiserveAuthorityPagamento: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.notaRiserveAuthorityPagamento);
        state.allOpenedTicket[index].notaRiserveAuthorityPagamento = { ...tckObject };
        state.allOpenedTicket[index].notaRiserveAuthorityPagamento = action.payload;
      });
    },
    setDisableComponentIncarico: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.disableComponentIncarico);
        state.allOpenedTicket[index].disableComponentIncarico = { ...tckObject };
        state.allOpenedTicket[index].disableComponentIncarico = action.payload;
      });
    },
    getListaSearchPolizza: (state, action) => {
      state.openedTicket.listaSearchPolizza = action.payload;
    },
    getListaSearchPolizzaCollettiva: (state, action) => {
      state.openedTicket.listaSearchPolizzaCollettiva = action.payload;
    },
    getListaBeniCollettiviAmministrativi: (state, action) => {
      state.openedTicket.listaBeniCollettiviAmministrativi = action.payload;
    },
    getListaSearchCollettiva: (state, action) => {
      state.openedTicket.listaSearchCollettiva = action.payload;
    },
    getListaSearchAdesioni: (state, action) => {
      state.openedTicket.listaSearchAdesioni = action.payload;
    },
    setPolizza: (state, action) => {
      state.openedTicket.setPolizza = action.payload;
    },
    setPolizzaTempAmministrativo: (state, action) => {
      state.openedTicket.setPolizzaTempAmministrativo = action.payload;
    },
    setNotaPromemoria: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiAppuntamento?.nota);
        state.allOpenedTicket[index].datiAppuntamento.nota = { ...tckObject };
        state.allOpenedTicket[index].datiAppuntamento.nota = action.payload;
      });
    },
    setDescrizionePromemoria: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiAppuntamento?.descrizione);
        state.allOpenedTicket[index].datiAppuntamento.descrizione = { ...tckObject };
        state.allOpenedTicket[index].datiAppuntamento.descrizione = action.payload;
      });
    },
    getListaPagamentiIstruttoria: (state, action) => {
      state.openedTicket.listaPagamenti = action.payload;
    },
    setAlertPagamentiSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.alertSimplePagamanetiSpese);
        state.allOpenedTicket[index].alertSimplePagamanetiSpese = { ...tckObject };
        state.allOpenedTicket[index].alertSimplePagamanetiSpese = action.payload;
      });
    },

    setAlertPagamentiIndennizzoBeneficiari: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.alertSimplePagamentiIndennizzoBeneficiari);
        state.allOpenedTicket[index].alertSimplePagamentiIndennizzoBeneficiari = { ...tckObject };
        state.allOpenedTicket[index].alertSimplePagamentiIndennizzoBeneficiari = action.payload;
      });
    },

    getListaDanneggiatiJmil: (state, action) => {
      state.openedTicket.listaDanneggiatiJmil = action.payload?.danneggiati;
    },
    setImportoSpesa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const pagamentoSpesaAll = state.allOpenedTicket[index].datiPagamento?.pagamentoSpesa || {};
        const pagamentoSpesaOpen = state.openedTicket?.datiPagamento?.pagamentoSpesa || {};

        // update allOpenedTicket
        state.allOpenedTicket[index].datiPagamento.pagamentoSpesa = {
          ...pagamentoSpesaAll,
          importo: action.payload,
        };

        // keep openedTicket fully in sync (preserve fields like statoPagamento)
        state.openedTicket.datiPagamento = state.openedTicket.datiPagamento || {};
        state.openedTicket.datiPagamento.pagamentoSpesa = {
          ...pagamentoSpesaOpen,
          importo: action.payload,
          statoPagamento: pagamentoSpesaAll.statoPagamento ?? pagamentoSpesaOpen.statoPagamento,
        };
      });
    },
    setIncaricoSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const pagamentoSpesaAll = state.allOpenedTicket[index].datiPagamento?.pagamentoSpesa || {};
        const pagamentoSpesaOpen = state.openedTicket?.datiPagamento?.pagamentoSpesa || {};

        // update allOpenedTicket
        state.allOpenedTicket[index].datiPagamento.pagamentoSpesa = {
          ...pagamentoSpesaAll,
          incaricato: action.payload,
        };

        // keep openedTicket fully in sync (preserve fields like statoPagamento)
        state.openedTicket.datiPagamento = state.openedTicket.datiPagamento || {};
        state.openedTicket.datiPagamento.pagamentoSpesa = {
          ...pagamentoSpesaOpen,
          incaricato: action.payload,
          statoPagamento: pagamentoSpesaAll.statoPagamento ?? pagamentoSpesaOpen.statoPagamento,
        };
      });
    },
    getGaranziePagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.garanziePagamentoIndennizzo);
        state.allOpenedTicket[index].garanziePagamentoIndennizzo = { ...tckObject };
        state.allOpenedTicket[index].garanziePagamentoIndennizzo = action.payload;
      });
    },
    setGaranziePagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.garanziePagamentoIndennizzoValue);
        state.allOpenedTicket[index].garanziePagamentoIndennizzoValue = { ...tckObject };
        state.allOpenedTicket[index].garanziePagamentoIndennizzoValue = action.payload;
      });
    },
    setDanneggiatoIdPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.danneggiatoIdPagamentoIndennizzo);
        state.allOpenedTicket[index].danneggiatoIdPagamentoIndennizzo = { ...tckObject };
        state.allOpenedTicket[index].danneggiatoIdPagamentoIndennizzo = action.payload;
      });
    },
    setFormEnable: (state, action) => {
      state.openedTicket.formEnable = action.payload;
    },
    getListaBeneficiariInennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.listaBeneficiariIndennizzo);
        state.allOpenedTicket[index].listaBeneficiariIndennizzo = { ...tckObject };
        state.allOpenedTicket[index].listaBeneficiariIndennizzo = action.payload;
      });
    },
    // store suggested dataUltimoPagamento per pagamentoId
    getSuggestedDate: (state, action) => {
      const { pagamentoId, payload } = action.payload || action;
      if (!pagamentoId) return;
      state.suggestedDates = {
        ...state.suggestedDates,
        [pagamentoId]: payload,
      };
    },
    setDatiValidazione: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiValidazionePut);
        state.allOpenedTicket[index].datiValidazionePut = { ...tckObject };
        state.allOpenedTicket[index].datiValidazionePut = action.payload;
      });
    },
    setIdValidazione: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.idEsitazioneValidazione);
        state.allOpenedTicket[index].idEsitazioneValidazione = { ...tckObject };
        state.allOpenedTicket[index].idEsitazioneValidazione = action.payload;
      });
    },
    setImportoPagamento: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.importoPagmento);
        state.allOpenedTicket[index].importoPagmento = { ...tckObject };
        state.allOpenedTicket[index].importoPagmento = action.payload;
      });
    },
    // CSP-2423: gross total of the indemnity payment, used for the authority checks
    setImportoLordoPagamento: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].importoLordoPagamento = action.payload;
      });
    },
    // CSP-2423: payment mode (net/gross of deductible) and deductible per garanzia of the indemnity payment
    setFranchigiaPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].franchigiaPagamentoIndennizzo = action.payload;
      });
    },
    setSelectedRadioIdPolizza: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.selectedRadioIdPolizza = action.payload;
        state.allOpenedTicket[index].selectedRadioIdPolizza = action.payload;
      });
    },
    setDuplicatoId: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.duplicatoId = action.payload;
        state.allOpenedTicket[index].duplicatoId = action.payload;
      });
    },
    getDatiUtente: (state, action) => {
      state.userData = {};
      state.userData = { ...{}, ...action.payload };
    },
    setGridConfigJson: (state, action) => {
      const datiConfig = state.userData.gridConfigJson;
      if (datiConfig && typeof datiConfig === 'object' && Object.keys(datiConfig).length > 0) { // Check if it's an object and has keys
        state.userData.gridConfigJson = action.payload;
      }
    },
    setGridFilterConfigJson: (state, action) => {
      const datiConfig = state.userData.gridConfigJson;
      if (datiConfig && typeof datiConfig === 'object') { // Check if it's an object
        state.userData.gridFilterConfigJson = action.payload;
      }
    },
    setIdTitoloAmmOpenSinistro: (state, action) => {//
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.idTitoloAmmOpenSinistro = action.payload;
        state.allOpenedTicket[index].idTitoloAmmOpenSinistro = action.payload;
      });
    },
    setPagamentiSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const currentOpened =
          state.openedTicket?.datiPagamento?.pagamentoSpesa || {};
        const currentAll =
          state.allOpenedTicket[index]?.datiPagamento?.pagamentoSpesa || {};

        // merge new data (id, incaricato, etc.) with existing BE fields (statoPagamento, esito, flags, ...)
        state.openedTicket.datiPagamento = state.openedTicket.datiPagamento || {};
        state.openedTicket.datiPagamento.pagamentoSpesa = {
          ...currentOpened,
          ...action.payload,
        };

        state.allOpenedTicket[index].datiPagamento =
          state.allOpenedTicket[index].datiPagamento || {};
        state.allOpenedTicket[index].datiPagamento.pagamentoSpesa = {
          ...currentAll,
          ...action.payload,
        };
      });
    },
    setEmailMittenteSelectState: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.emailMittente = action.payload;
        state.allOpenedTicket[index].emailMittente = action.payload;
      });
    },
    setAssegnatarioSelecState: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.assegnatarioSinistroAperto = action.payload;
        state.allOpenedTicket[index].assegnatarioSinistroAperto = action.payload;
      });
    },
    setSelectedGaranzieIngresso: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.selectedGaranzieIngresso = action.payload || [];
        state.allOpenedTicket[index].selectedGaranzieIngresso = action.payload || [];
      });
    },
    clearSelectedGaranzieIngresso: (state) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.selectedGaranzieIngresso = [];
        state.allOpenedTicket[index].selectedGaranzieIngresso = [];
      });
    },
    setListDuplicati: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaDuplicati = action.payload;
        state.allOpenedTicket[index].listaDuplicati = action.payload;
      });
    },
    setEsitoPerPostEsitazione: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.esitoPerPostEsitazione = action.payload;
        state.allOpenedTicket[index].esitoPerPostEsitazione = action.payload;
      });
    },
    setRiservaNettaLorda: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiDenuncia.gestioneSinistro = action.payload;
        state.allOpenedTicket[index].datiDenuncia.gestioneSinistro = action.payload;
      });
    },
    getCalcoloMassimali: (state, action) => {
      state.calcoloMassimaliExtOpnenedticket = action.payload;
    },
    setRecuperoFranchigia: (state, action) => {
      state.recuperoFranchigia = action.payload;
    },
    setTotaleRecuperoFranchigia: (state, action) => {
      state.totaleRecuperoFranchigia = action.payload;
    },
    setRecuperoFranchigiaIstruttoria: (state, action) => {
      state.openedTicket.recuperoFranchigiaIstruttoria = action.payload;
    },
    setCheckWorldStatus: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const updateOpenedTicketIndennizzo = state.openedTicket.datiPagamento.pagamentoIndennizzo;
        const updateAllOpenedTicketIndennizzo = state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo;
        const updateOpenedTicketSpese = state.openedTicket.datiPagamento.pagamentoSpese;
        const updateAllOpenedTicketSpese = state.allOpenedTicket[index].datiPagamento.pagamentoSpese;
        console.log('action.payload setCheckWorldStatus', action.payload)
        if (updateAllOpenedTicketIndennizzo) {
          state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = {
            ...updateAllOpenedTicketIndennizzo,
            esitoWorldcheck: action.payload.checked
          }
          state.openedTicket.datiPagamento.pagamentoIndennizzo = {
            ...updateOpenedTicketIndennizzo,
            esitoWorldcheck: action.payload.checked
          }
          state.checkWorld = {
            checked: action.payload.checked,
            status: action.payload.status || '',
            gridValues: action.payload.gridValues || [],
            dataEsecuzione: action.payload.dataEsecuzione
          };
        }
        if (updateAllOpenedTicketSpese) {
          state.allOpenedTicket[index].datiPagamento.pagamentoSpese = {
            ...updateAllOpenedTicketSpese,
            esitoWorldcheck: action.payload.checked
          }
          state.openedTicket.datiPagamento.pagamentoSpese = {
            ...updateOpenedTicketSpese,
            esitoWorldcheck: action.payload.checked
          }
          state.checkWorld = {
            checked: action.payload.checked,
            status: action.payload.status || '',
            gridValues: action.payload.gridValues || [],
            dataEsecuzione: action.payload.dataEsecuzione
          };
        }
      });
      state.checkWorld = {
        checked: action.payload.checked,
        status: action.payload.status || '',
        gridValues: action.payload.gridValues || [],
        dataEsecuzione: action.payload.dataEsecuzione
      }
    },
    updateWorldStatus: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const updateOpenedTicketIndennizzo = state.openedTicket.datiPagamento.pagamentoIndennizzo;
        const updateAllOpenedTicketIndennizzo = state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo;
        const updateOpenedTicketSpese = state.openedTicket.datiPagamento.pagamentoSpese;
        const updateAllOpenedTicketSpese = state.allOpenedTicket[index].datiPagamento.pagamentoSpese;
        if (updateAllOpenedTicketIndennizzo) {
          state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = {
            ...updateAllOpenedTicketIndennizzo,
            esito: action.payload,
            esitoWorldcheck: state.checkWorld.checked
          }
          state.openedTicket.datiPagamento.pagamentoIndennizzo = {
            ...updateOpenedTicketIndennizzo,
            esito: action.payload,
            esitoWorldcheck: state.checkWorld.checked
          }
        }
        if (updateAllOpenedTicketSpese) {
          state.allOpenedTicket[index].datiPagamento.pagamentoSpese = {
            ...updateAllOpenedTicketSpese,
            esito: action.payload,
            esitoWorldcheck: state.checkWorld.checked
          }
          state.openedTicket.datiPagamento.pagamentoSpese = {
            ...updateOpenedTicketSpese,
            esito: action.payload,
            esitoWorldcheck: state.checkWorld.checked
          }
        }
        state.checkWorld.status = action.payload;
      });
    },
    toggleIsButtonEnabledMassimaleGaranzieVisible: (state, action) => {
      state.isButtonEnabledMassimaleGaranzieVisible = action.payload;
    },
    setListaBeneficiariRecuperoFranchigia: (state, action) => {
      state.listaBeneficiariRecuperoFranchigia = action.payload;
    },
    setListaBeneficiariRecuperoFranchigiaTable: (state, action) => {
      state.listaBeneficiariRecuperoFranchigiaTable = action.payload;
    },
    setListaDebitoreRecuperoRivalsa: (state, action) => {
      state.listaDebitoreRecuperoRivalsa = action.payload;
    },
    setSelectRecuperoFranchigia: (state, action) => {
      state.openedTicket.selectRecuperoFranchigia = action.payload;
    },
    getListaAllegatiPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaAllegatiPagamentoIndennizzo = action.payload;
        state.allOpenedTicket[index].listaAllegatiPagamentoIndennizzo = action.payload;
      });
    },
    getListaAllegatiQuietanza: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaAllegatiQuietanza = action.payload;
        state.allOpenedTicket[index].listaAllegatiQuietanza = action.payload;
      });
    },
    getAllegatoPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.allegatoPagamentoIndennizzo = action.payload;
        state.allOpenedTicket[index].allegatoPagamentoIndennizzo = action.payload;
      });
    },
    getAllIncassi: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.allIncassi = action.payload;
        state.allOpenedTicket[index].allIncassi = action.payload;
      });
    },
    getAllIncassiRivalsa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.allIncassiRivalsa = action.payload;
        state.allOpenedTicket[index].allIncassiRivalsa = action.payload;
      });
    },
    setCheckTotaleIncassi: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].allIncassi.flagTotale = action.payload;
      });
    },
    getListaAllegatiPagoPa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaAllegatoPagoPA = action.payload;
        state.allOpenedTicket[index].listaAllegatoPagoPA = action.payload;
      });
    },
    getListaAllegatiPagamentoSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaAllegatiPagamentoSpese = action.payload;
        state.allOpenedTicket[index].listaAllegatiPagamentoSpese = action.payload;
      });
    },
    getListaAllegatiInformativaPrivacy: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.listaAllegatiInformativaPrivacy = action.payload;
        state.allOpenedTicket[index].listaAllegatiInformativaPrivacy = action.payload;
      });
    },
    setQuietanzaSeparata: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.quietanzaSeparata);
        state.allOpenedTicket[index].quietanzaSeparata = { ...tckObject };
        state.allOpenedTicket[index].quietanzaSeparata = action.payload;
      });
    },
    setPagamentoFatturaIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.pagamentoFatturaIndennizzo = action.payload;
        state.allOpenedTicket[index].pagamentoFatturaIndennizzo = action.payload;
      });
    },
    popolaDatiShellClaim: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.datiShellClaim);
        state.allOpenedTicket[index].datiShellClaim = { ...tckObject };
        state.allOpenedTicket[index].datiShellClaim = action.payload;
      });
    },
    aggiornaCalcoliShellClaim: (state) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiShellClaim;
        if (dati) {
          dati.riservaIndennizzoNostraQuota = (dati.nostraQuota / 100) * dati.riservaIndennizzo;
          dati.riservaSpeseNostraQuota = (dati.nostraQuota / 100) * dati.riservaSpese;
          dati.riservaTotale = dati.riservaIndennizzo + dati.riservaSpese;
          dati.riservaTotaleNostraQuota = dati.riservaIndennizzoNostraQuota + dati.riservaSpeseNostraQuota;
          dati.pagatoTotaleNostraQuota = dati.pagatoIndennizzoNostraQuota + dati.pagatoSpeseNostraQuota
          state.allOpenedTicket[index].datiShellClaim = { ...state.allOpenedTicket[index].datiShellClaim, ...dati };
          state.openedTicket.datiShellClaim = { ...state.openedTicket?.datiShellClaim, ...dati };
        }
      });
    },
    setuserDataShellClaimLiquidatore: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index];
        if (dati) {
          const { codIspettoratoJmil } = action.payload;
          const { codLiquidatoreJmil } = action.payload;
          dati.userDataShellClaimLiquidatore = { codIspettoratoJmil, codLiquidatoreJmil };

          state.allOpenedTicket[index] = { ...state.allOpenedTicket[index], ...dati };
          state.openedTicket = { ...state.openedTicket, ...dati };
        }
      });
    },

    setDescrAssicuratoDatiShellClaim: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiShellClaim;
        if (dati) {
          dati.assicurato = action.payload;
        }
      });
    },
    setIdTitoloBeneCollettiveShellClaim: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiShellClaim;
        if (dati) {
          dati.idBeneCollettivo = action.payload;
        }
      });
    },
    setIdTitoloAmmShellClaim: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiShellClaim;
        if (dati) {
          dati.idTitoloAmministrativo = action.payload;
        }
      });
    },
    setSinistroIngente: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        if (state.openedTicket.datiDenuncia) {
          state.openedTicket.datiDenuncia.sinistroIngente = action.payload;
          state.allOpenedTicket[index].datiDenuncia.sinistroIngente = action.payload;
        }
      });
    },
    setValidazioneNotaResponsabile: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiValidazione.notaResponsabile = action.payload;
        state.allOpenedTicket[index].datiValidazione.notaResponsabile = action.payload;
      });
    },
    setRiservaDiretta: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.datiIncarico.gestioneRiserva = action.payload;
        state.allOpenedTicket[index].datiIncarico.gestioneRiserva = action.payload;
      });
    },
    setBeneCollettiveAmministrativoToDannObj: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.beneCollettivoAmministrativoToDann = action.payload;
        state.allOpenedTicket[index].beneCollettivoAmministrativoToDann = action.payload;
      });
    },
    setIdBeneAssicuratoAmministrativo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const datiDenunciaWrite = state.allOpenedTicket[index].datiDenuncia;
        const datiDenunciaRead = state.openedTicket.datiDenuncia;
        if (datiDenunciaRead && datiDenunciaWrite) {
          datiDenunciaRead.idBene = action.payload;
          datiDenunciaWrite.idBene = action.payload;
        }
      });
    },
    multiValutaPagamentoSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.multiValutaPagSpese);
        state.allOpenedTicket[index].multiValutaPagSpese = { ...tckObject };
        state.allOpenedTicket[index].multiValutaPagSpese = { ...action.payload };
      });
    },
    multiValutaPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket?.multiValutaPagamentoIndennizzo);
        state.allOpenedTicket[index].multiValutaPagamentoIndennizzo = { ...tckObject };
        state.allOpenedTicket[index].multiValutaPagamentoIndennizzo = { ...action.payload };
      });
    },
    setCcMail: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].ccMail = action.payload;  // creo array con le mail inserite
        state.allOpenedTicket[index].ccMailValid = true;//inizio con true
        state.allOpenedTicket[index].ccMailErrors = [];//inizializzo array vuoto
      });
    },
    validateCcMail: (state) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index];
        const uniqueEmails = [...dati.ccMail];//creo array con la mail inserita
        const validEmails = uniqueEmails.filter(email => profileValidation.isValidEmailNew(email));//filtro le mail valide
        const invalidEmails = uniqueEmails.filter(email => !profileValidation.isValidEmailNew(email));//filtro le mail non valide
        dati.ccMailValid = invalidEmails.length === 0;//se la lunghezza delle mail non valide è 0 allora è true
        dati.ccMailErrors = invalidEmails;//salvo le mail non valide
        dati.validCcMails = validEmails; //salvo  nello stato le mail valide
      });
    },
    setNotaEsitoInAgenda: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket);
        state.allOpenedTicket[index] = { ...tckObject };
        const dati = state.allOpenedTicket[index];
        if (dati) {
          dati.notaEsitoInAgenda = action.payload;
        }
      });
    },
    setNotaAppuntamento: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket);
        state.allOpenedTicket[index] = { ...tckObject };
        const dati = state.allOpenedTicket[index];
        if (dati) {
          dati.notaAppuntamento = action.payload;
        }
      });
    },
    setOperatori: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const tckObject = Object.assign({}, state.openedTicket);
        state.allOpenedTicket[index] = { ...tckObject };
        const dati = state.allOpenedTicket[index];
        if (dati) {
          dati.listaOperatori = action.payload;
        }
      });
    },
    setRiassegnaoperatoreSinistro: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index];
        if (dati) {
          dati.operatoreRiassegnato = action.payload;
          state.allOpenedTicket[index] = { ...dati, ...action.payload };
        }
      });
    },
    setObjNewTaskDenuncia: (state, action) => {
      state.objNewTask = {};
      state.objNewTask = { ...{}, ...action.payload };
    },
    setIdRadioButtonNewTask: (state, action) => {
      state.idRadioButtonNewTask = action.payload;
    },
    setDerogaAllegato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiPagamento?.pagamentoIndennizzo;
        if (dati) {
          dati.derogaAllegato = action.payload;
          state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = { ...dati };
        }
      });
    },
    // CSP-3136 - aggiorna in redux la scelta "richiesto documento quietanza" (SI/NO)
    // così il gate di emissione (selectState8) può imporre il documento obbligatorio.
    setRichiestoDocumentoQuietanzaTicket: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiPagamento?.pagamentoIndennizzo;
        if (dati) {
          dati.richiestoDocumentoQuietanza = action.payload;
          state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = { ...dati };
        }
      });
    },
    setDocumentoQuietanzaConfermato: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiPagamento?.pagamentoIndennizzo;
        if (dati) {
          dati.documentoQuietanzaConfermato = action.payload;
          state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = { ...dati };
        }
      });
    },
    getValidazioneIban: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index];
        if (dati) {
          dati.validazioneIban = action.payload;
          state.allOpenedTicket[index] = { ...dati };
        }
      });
    },
    setConfigLanguage: (state, action) => {
      //eslint-disable-next-line eqeqeq
      state.language = action.payload;
      localStorage.setItem('lang', action.payload.language);
    },
    setListaCodiceEvento: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const dati = state.allOpenedTicket[index].datiDenuncia;
        const datiRead = state.openedTicket.datiDenuncia;
        if (dati && datiRead) {
          dati.listaCodiceEvento = action.payload;
          datiRead.listaCodiceEvento = action.payload;
          state.allOpenedTicket[index].datiDenuncia = { ...dati };
          state.openedTicket.datiDenuncia = { ...datiRead };
        }
      });
    },
    setAlertInventarioRiserva: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const datiWrite = state.allOpenedTicket[index]
        if (datiWrite) {
          datiWrite.alertInventarioRiserva = action.payload;
          state.allOpenedTicket[index] = { ...datiWrite };
        }
      });
    },
    setDatiStornoPagamentoIndennizzo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.allOpenedTicket[index].datiPagamento,
          pagamentoIndennizzo: {
            ...state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo,
            ...action.payload,
          }
        };
        const newDatiRead = {
          ...state.openedTicket.datiPagamento,
          pagamentoIndennizzo: {
            ...state.openedTicket.datiPagamento.pagamentoIndennizzo,
            ...action.payload,
          }
        };
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          datiPagamento: newDati
        };
        state.openedTicket = {
          ...state.openedTicket,
          datiPagamento: newDatiRead
        };
      });
    },
    setDatiStornoPagamentoSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.allOpenedTicket[index].datiPagamento,
          pagamentoSpesa: {
            ...state.allOpenedTicket[index].datiPagamento.pagamentoSpesa,
            ...action.payload,
          }
        };
        const newDatiRead = {
          ...state.openedTicket.datiPagamento,
          pagamentoSpesa: {
            ...state.openedTicket.datiPagamento.pagamentoSpesa,
            ...action.payload,
          }
        };
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          datiPagamento: newDati
        };
        state.openedTicket = {
          ...state.openedTicket,
          datiPagamento: newDatiRead
        };
      });
    },
    setDatiNotaCreditoPagamentoSpese: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.allOpenedTicket[index].datiPagamento,
          pagamentoSpesa: {
            ...state.allOpenedTicket[index].datiPagamento.pagamentoSpesa,
            ...action.payload,
          }
        };
        const newDatiRead = {
          ...state.openedTicket.datiPagamento,
          pagamentoSpesa: {
            ...state.openedTicket.datiPagamento.pagamentoSpesa,
            ...action.payload,
          }
        };
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          datiPagamento: newDati
        };
        state.openedTicket = {
          ...state.openedTicket,
          datiPagamento: newDatiRead
        };
      });
    },
    setRiservaSpesaIncarico: (state, action) => {
      // CSP-3544: la riserva spese si varia anche dal task pagamento spese (senza datiIncarico) e il
      // toast puo` arrivare con un altro task aperto: si aggiorna solo l'incarico di quella riserva.
      const { riservaSpesaId, ...riservaAggiornata } = action.payload || {};
      updateTicketIfFound(state, (state, index) => {
        const isRiservaDelTask = (datiIncarico) => !!riservaSpesaId
          && String(datiIncarico?.riservaSpesa?.id) === String(riservaSpesaId);
        if (!isRiservaDelTask(state.allOpenedTicket[index].datiIncarico)
          || !isRiservaDelTask(state.openedTicket.datiIncarico)) return;
        const newDati = {
          ...state.allOpenedTicket[index].datiIncarico,
          riservaSpesa: {
            ...state.allOpenedTicket[index].datiIncarico.riservaSpesa,
            ...riservaAggiornata,
          }
        };
        const newDatiRead = {
          ...state.openedTicket.datiIncarico,
          riservaSpesa: {
            ...state.openedTicket.datiIncarico.riservaSpesa,
            ...riservaAggiornata,
          }
        };
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          datiIncarico: newDati
        };
        state.openedTicket = {
          ...state.openedTicket,
          datiIncarico: newDatiRead
        };
      });
    },
    getAvailableInstantPayment: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.openedTicket.availableInstantPayment = action.payload;
        state.allOpenedTicket[index].availableInstantPayment = action.payload;
      });
    },
    setRecuperoGaranzieRivalsa: (state, action) => {
      updateTicketIfFound(state, (state) => {
        const newDati = state.openedTicket;
        if (newDati) {
          newDati.recuperoGaranzieRivalsa = action.payload;
          state.openedTicket = { ...newDati };
        }
      });
    },
    setImportoTotaleGaranzieRivalsa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = state.allOpenedTicket[index];
        if (newDati) {
          newDati.importoTotaleGaranzieRivalsa = action.payload;
          state.allOpenedTicket[index] = { ...newDati };
        }
      });
    },
    setRecuperoDatiRivalsa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.allOpenedTicket[index].datiRecupero,
          recuperoRivalsa: {
            ...state.allOpenedTicket[index].datiRecupero.recuperoRivalsa,
            ...action.payload,
          }
        };
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          datiRecupero: newDati
        };
      });
    },
    setMotivazioneClausolaSirSottoSir: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.allOpenedTicket[index].clausolaSir,
          ...action.payload,
        }
        if (newDati) {
          state.allOpenedTicket[index] = {
            ...state.allOpenedTicket[index],
            ...newDati
          };
        }
      });
    },
    setAlertSopraSir: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const datiWrite = state.allOpenedTicket[index]
        if (datiWrite) {
          datiWrite.isAlertSopraSirOpen = action.payload;
          state.allOpenedTicket[index] = { ...datiWrite };
        }
      });
    },
    setVerificaAmministrativaBene: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const newDati = {
          ...state.verificaAmministrativaBene,
          ...action.payload,
        }
        const allOpenedTicketDatiDenuncia = state.allOpenedTicket[index].datiDenuncia
        const openedTicketDatiDenuncia = state.openedTicket.datiDenuncia
        if (newDati) {
          state.verificaAmministrativaBene = {
            ...state.verificaAmministrativaBene,
            ...newDati
          };
        }
        if (allOpenedTicketDatiDenuncia) {
          allOpenedTicketDatiDenuncia.motivazioneBeneCollettivo = newDati.motivazione;
          state.allOpenedTicket[index].datiDenuncia = { ...allOpenedTicketDatiDenuncia };
        }
        if (openedTicketDatiDenuncia) {
          openedTicketDatiDenuncia.motivazioneBeneCollettivo = newDati.motivazione;
          state.openedTicket.datiDenuncia = { ...openedTicketDatiDenuncia };
        }
      });
    },
    setEsitoSanzionePagamenti: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        const allOpenedTicketDatiPagamento = state.allOpenedTicket[index].datiPagamento
        const openedTicketDatiPagamento = state.openedTicket.datiPagamento
        if (allOpenedTicketDatiPagamento?.pagamentoIndennizzo && openedTicketDatiPagamento?.pagamentoIndennizzo) {
          allOpenedTicketDatiPagamento.pagamentoIndennizzo.esito = action.payload;
          state.allOpenedTicket[index].datiPagamento = { ...allOpenedTicketDatiPagamento };
          openedTicketDatiPagamento.pagamentoIndennizzo.esito = action.payload;
          state.openedTicket.datiPagamento = { ...openedTicketDatiPagamento };
        } else {
          allOpenedTicketDatiPagamento.pagamentoSpesa.esito = action.payload;
          state.allOpenedTicket[index].datiPagamento = { ...allOpenedTicketDatiPagamento };
          openedTicketDatiPagamento.pagamentoSpesa.esito = action.payload;
          state.openedTicket.datiPagamento = { ...openedTicketDatiPagamento };
        }
      });
    },
    setMotivazioneSenzaSeguito: (state, action) => {
      state.motivazioneSenzaSeguito = action.payload;
    },
    // CSP-3170: DDL dei motivi di chiusura senza seguito (usata dal flusso "chiudi e clona").
    setMotiviSenzaSeguito: (state, action) => {
      state.motiviSenzaSeguito = action.payload;
    },
    // CSP-3170: catena clonazioni del sinistro corrente.
    setCatenaClonazioni: (state, action) => {
      state.catenaClonazioni = action.payload;
    },
    setCodiceMotivazioneSenzaSeguito: (state, action) => {
      state.codiceMotivazioneSenzaSeguito = action.payload;
    },
    // CSP-2423: { righe, lordi } of the damage entirely in deductible, null when not used
    setDannoFranchigiaSenzaSeguito: (state, action) => {
      state.dannoFranchigiaSenzaSeguito = action.payload;
    },
    setDataLiquidazioneRedux: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiDenuncia = {
          ...state.allOpenedTicket[index].datiDenuncia,
          dataLiquidazioneAnticipata: action.payload
        };

        if (state.openedTicket?.datiDenuncia) {
          state.openedTicket.datiDenuncia = {
            ...state.openedTicket.datiDenuncia,
            dataLiquidazioneAnticipata: action.payload,
          };
        }
      });
    },
    setStatoRichiestaAnticipo: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiDenuncia = {
          ...state.allOpenedTicket[index].datiDenuncia,
          statoRichiestaAnticipo: action.payload
        };
        if (state.openedTicket?.datiDenuncia) {
          state.openedTicket.datiDenuncia = {
            ...state.openedTicket.datiDenuncia,
            statoRichiestaAnticipo: action.payload,
          };
        }
      });
    },
    setCoperturaTecnicaForzata: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiDenuncia = {
          ...state.allOpenedTicket[index].datiDenuncia,
          coperturaTecnicaForzata: action.payload,
        };
        if (state.openedTicket?.datiDenuncia) {
          state.openedTicket.datiDenuncia = {
            ...state.openedTicket.datiDenuncia,
            coperturaTecnicaForzata: action.payload,
          };
        }
      });
    },
    // CSP-3170: persisted confirmation of the settlement regimes check before "chiudi e clona".
    setConfermaRegimiClona: (state, action) => {
      const { confermaRegimiClona, dataConfermaRegimiClona, utenteConfermaRegimiClona } = action.payload;
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiDenuncia = {
          ...state.allOpenedTicket[index].datiDenuncia,
          confermaRegimiClona,
          dataConfermaRegimiClona,
          utenteConfermaRegimiClona,
        };
        if (state.openedTicket?.datiDenuncia) {
          state.openedTicket.datiDenuncia = {
            ...state.openedTicket.datiDenuncia,
            confermaRegimiClona,
            dataConfermaRegimiClona,
            utenteConfermaRegimiClona,
          };
        }
      });
    },
    togglePopupLiquidazione: (state, action) => {
      state.anticipoLiquidazione.popupLiquidazione = action.payload
    },
    openPopupFattura: (state, action) => {
      // title è opzionale: se assente il popup usa i titoli di default del salvataggio
      // (vedi ToastContainer); serve ad esempio per l'esito dell'eliminazione fattura.
      state.popupFattura = {
        open: true,
        type: action.payload?.type || 'success',
        message: action.payload?.message || '',
        ...(action.payload?.title ? { title: action.payload.title } : {}),
      };
    },
    closePopupFattura: (state) => {
      state.popupFattura = {
        open: false,
        type: 'success',
        message: '',
      };
    },
    setFlagLiquidazioneAnticipata: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo = {
          ...state.allOpenedTicket[index].datiPagamento.pagamentoIndennizzo,
          flagLiquidazioneAnticipata: action.payload
        };
      });
    },
    setIsLiquidabileBeSafe: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        let allOpenedTicketDatiBeSafe = state.allOpenedTicket[index]?.datiDenuncia;
        let openedTicketDatiBeSafe = state.openedTicket?.datiDenuncia;
        if (!allOpenedTicketDatiBeSafe) {
          state.allOpenedTicket[index].datiDenuncia = { ...state.allOpenedTicket[index].datiDenuncia, isLiquidabile: action.payload };
          allOpenedTicketDatiBeSafe = state.allOpenedTicket[index]?.datiDenuncia;
        }
        if (!openedTicketDatiBeSafe) {
          state.openedTicket.datiDenuncia = { ...state.openedTicket.datiDenuncia, isLiquidabile: action.payload };
          openedTicketDatiBeSafe = state.openedTicket?.datiDenuncia;
        }
        if (allOpenedTicketDatiBeSafe) {
          allOpenedTicketDatiBeSafe.isLiquidabile = action.payload;
          state.allOpenedTicket[index].datiDenuncia = { ...allOpenedTicketDatiBeSafe };
        }
        if (openedTicketDatiBeSafe) {
          openedTicketDatiBeSafe.isLiquidabile = action.payload;
          state.openedTicket.datiDenuncia = { ...openedTicketDatiBeSafe };
        }
      })
    },
    setRispondiEmailPayloadForEntity: (state, action) => {
      state.openedTicket.emailPayloadRispostaForEntity = action.payload;
    },
    setEmailTipologie: (state, action) => {
      state.emailTipologie = action.payload || [];
    },
    setSelectedAttach: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          selectedAttach: action.payload
        };
      });
    },
    setFirstItemOfIncarichiList: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          firstItemOfIncarichiList: action.payload
        }
      })
    },
    listaDanneggiatiSinistroCausa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          listaDanneggiatiSinistroCausa: action.payload
        }
      })
    },
    listaIncarichiSinistroCausa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          listaIncarichiSinistroCausa: action.payload
        }
      })
    },
    listaIncarichiSelezionatiCausa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          listaIncarichiSelezionatiCausa: [...action.payload]
        }
      })
    },
    danneggiatoSelezionatoCausa: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          danneggiatoSelezionatoCausa: [action.payload]
        }
      })
    },
    setCausaValidazioneApertura: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index] = {
          ...state.allOpenedTicket[index],
          causaValidazioneApertura: action.payload
        }
      })
    },
    getDiniego: (state, action) => {
      if (state.openedTicket) {
        state.openedTicket.diniego = action.payload;
      }
    },
    getSepaCountries: (state, action) => {
      state.sepaCountries = action.payload;
    },
	getAttachmentsEmail: (state, action) => {
	  updateTicketIfFound(state, (state) => {
	    state.openedTicket.attachmentsEmail = action.payload;
	  });
	},
    setTipoIncaricoIdCompetenzaIdSpecializzazioneId: (state, action) => {
      updateTicketIfFound(state, (state, index) => {
        state.allOpenedTicket[index].datiIncarico = {
          ...state.allOpenedTicket[index].datiIncarico,
          idTipoIncarico: action.payload.idTipoIncarico,
          idCompetenza: action.payload.idCompetenza,
          idSpecializzazione: action.payload.idSpecializzazione
        }
      })
    }
  },
});

export const {
  setDataNotificaRedux,
  setCoperturaTecnicaForzata,
  setConfermaRegimiClona,
  setTicketsLavorabiliCompleti,
  setLavorabiliCompletiLoading,
  invalidateLavorabiliCompleti,
  getTicketsLavorabili,
  setPaginationLavorabili,
  // Ricerca ibrida:
  setSearchedTickets,
  setSearchPagination,
  setSearchFilters,
  setSearchLoading,
  resetSearch,
  // Deprecato ma esportato per retrocompatibilità:
  getTicketsLavorabiliFull,
  getTicketsScadenziario,
  getTicketsScarti,
  getTicketsRevisioneSap,
  getTicketsChatUnread,
  getTicket,
  getTicketTree,
  getTicketsAssegnate,
  setVisibilityFilter,
  SearchTicket,
  DeleteTicket,
  setOpenedTicketClaimData,
  listaDanneggiatiSinistroCausa,
  listaIncarichiSinistroCausa,
  listaIncarichiSelezionatiCausa,
  danneggiatoSelezionatoCausa,
  setCausaValidazioneApertura,
  setTipoIncaricoIdCompetenzaIdSpecializzazioneId,
  setOpenedTicketAdminData,
  setOpenedTicketAdminDataRender,
  setOpenedTicketNPolizza,
  setConfigTipiIncarico,
  setFiduciariConfig,
  setAreaGeograficaOptions,
  setAreaGeograficaLoading,
  clearDatiDenuncia,
  setOpenedTicketDatiCausa,
  setOpenedTicketDatiIncarico,
  setDenunciaMotivazione,
  setIdTicketInCharge,
  removeIdTicketInCharge,
  addOpenedTicket,
  setOpenedTicketChangePolizza,
  setOpenedTicketDatiTask,
  addTaskToNavSinstro,
  addOpenedTicketCached,
  removeOpenedTicket,
  setModifiedTicket,
  resetModifiedTicket,
  toogleLoading,
  toogleLoadingGrid,
  setListLoading,
  setListLoaded,
  markTicketListsDirty,
  resetTicketListsLoaded,
  setIncaricatiSearchLoading,
  setIncaricatiSearchError,
  getAvailableInstantPayment,
  toggleIsSlowFormVisible,
  getListaDanneggiati,
  getListaBeneficiarioLqInd,
  getListaSoggettiCause,
  setListaSoggettiCausa,
  getRamiSinistro,
  setTipologiaIncaricato,
  VisibleAlertDangerMora,
  VisibleAlertWarningMora,
  setFirstItemOfIncarichiList,
  VisibleAlertNoDataEvDataRis,
  setTitoloIsInCopertura,
  getGaranzieDanneggiato,
  setRamoGaranzia,
  setRamoGaranziaDanneggiato,
  setSelectedGaranzieIngresso,
  clearSelectedGaranzieIngresso,
  setDanneggiatoId,
  setProntaLiquidazione,
  getPrecedentiSinistri,
  getRiserveComplessive,
  getCauseIstruttoria,
  getIncarichiIstruttoria,
  getValidazioniIstruttoria,
  addIncaricato,
  removeIncaricato,
  showAlertIncaricato,
  setTipoIncaricato,
  getListaIncaricatiInQuadro,
  getListaIncaricatoNoQuadro,
  setIdCauseCollegate,
  getRiservaSpeseIncarico,
  setMotivazioneIncarico,
  setMotivazioneIncaricoNoQuadro,
  setNotaRiserveAuthority,
  setNotaRiserveAuthorityPagamento,
  setDisableComponentIncarico,
  getListaSearchPolizza,
  getListaSearchPolizzaCollettiva,
  getListaBeniCollettiviAmministrativi,
  getListaSearchCollettiva,
  getListaSearchAdesioni,
  setPolizza,
  setPolizzaTempAmministrativo,
  setNotaPromemoria,
  setDescrizionePromemoria,
  setCheckWorldStatus,
  updateWorldStatus,
  getListaPagamentiIstruttoria,
  setAlertPagamentiSpese,
  getListaDanneggiatiJmil,
  setImportoSpesa,
  setIncaricoSpese,
  getGaranziePagamentoIndennizzo,
  setGaranziePagamentoIndennizzo,
  setDanneggiatoIdPagamentoIndennizzo,
  setFormEnable,
  getListaBeneficiariInennizzo,
  getSuggestedDate,
  setAlertPagamentiIndennizzoBeneficiari,
  setDatiValidazione,
  setIdValidazione,
  setImportoPagamento,
  setImportoLordoPagamento,
  setFranchigiaPagamentoIndennizzo,
  setCodiceMotivazioneSenzaSeguito,
  setDannoFranchigiaSenzaSeguito,
  setSelectedRadioIdPolizza,
  setDuplicatoId,
  getDatiUtente,
  setGridConfigJson,
  setGridFilterConfigJson,
  setIdTitoloAmmOpenSinistro,
  setPagamentiSpese,
  setEmailMittenteSelectState,
  setLanguageTemplateEmail,
  setAssegnatarioSelecState,
  setListDuplicati,
  setEsitoPerPostEsitazione,
  setRiservaNettaLorda,
  getCalcoloMassimali,
  setRecuperoFranchigia,
  setRecuperoFranchigiaIstruttoria,
  setTotaleRecuperoFranchigia,
  setListaBeneficiariRecuperoFranchigia,
  toggleIsButtonEnabledMassimaleGaranzieVisible,
  setListaBeneficiariRecuperoFranchigiaTable,
  setSelectRecuperoFranchigia,
  setListaDebitoreRecuperoRivalsa,
  getListaAllegatiPagamentoIndennizzo,
  getAllegatoPagamentoIndennizzo,
  getListaAllegatiQuietanza,
  getListaAllegatiPagoPa,
  getListaAllegatiPagamentoSpese,
  setQuietanzaSeparata,
  setPagamentoFatturaIndennizzo,
  aggiornaCalcoliShellClaim,
  setDescrAssicuratoDatiShellClaim,
  setuserDataShellClaimLiquidatore,
  setIdTitoloBeneCollettiveShellClaim,
  setIdTitoloAmmShellClaim,
  popolaDatiShellClaim,
  setSinistroIngente,
  setValidazioneNotaResponsabile,
  setRiservaDiretta,
  setBeneCollettiveAmministrativoToDannObj,
  setIdBeneAssicuratoAmministrativo,
  multiValutaPagamentoSpese,
  multiValutaPagamentoIndennizzo,
  setCcMail,
  validateCcMail,
  setNotaEsitoInAgenda,
  setNotaAppuntamento,
  setOperatori,
  setRiassegnaoperatoreSinistro,
  setObjNewTaskDenuncia,
  setIdRadioButtonNewTask,
  setDerogaAllegato,
  setRichiestoDocumentoQuietanzaTicket,
  setDocumentoQuietanzaConfermato,
  getValidazioneIban,
  setConfigLanguage,
  setListaCodiceEvento,
  getListaAllegatiInformativaPrivacy,
  getAllIncassi,
  getAllIncassiRivalsa,
  setCheckTotaleIncassi,
  setAlertInventarioRiserva,
  setDatiStornoPagamentoIndennizzo,
  setDatiStornoPagamentoSpese,
  setDatiNotaCreditoPagamentoSpese,
  setRiservaSpesaIncarico,
  setRecuperoGaranzieRivalsa,
  setImportoTotaleGaranzieRivalsa,
  setRecuperoDatiRivalsa,
  setMotivazioneClausolaSirSottoSir,
  setAlertSopraSir,
  setVerificaAmministrativaBene,
  setEsitoSanzionePagamenti,
  setMotivazioneSenzaSeguito,
  setMotiviSenzaSeguito,
  setCatenaClonazioni,
  setMotivazioniAnnullamentoIncarico,
  setMotivazioniAnnullamentoIncaricoLoading,
  setMotivazioneAnnullamentoIncaricoId,
  setIsLiquidabileBeSafe,
  setDataLiquidazioneRedux,
  setStatoRichiestaAnticipo,
  togglePopupLiquidazione,
  openPopupFattura,
  closePopupFattura,
  setFlagLiquidazioneAnticipata,
  setRispondiEmailPayloadForEntity,
  setEmailTipologie,
  setSelectedAttach,
  getDiniego,
  getSepaCountries,
  getAttachmentsEmail
} = TicketSlice.actions;

/* =====================================================================
 * Cache lato client delle ricerche lavorabili gia' eseguite.
 *
 * Ripetere la STESSA ricerca (stessi page/size/filtri) entro la TTL riusa
 * il risultato tenuto in memoria, senza ri-chiamare il BFF (quindi senza
 * ri-colpire la SP CContact light). NON e' una sorgente dati alternativa:
 * la sorgente resta CContact, questo e' solo un memo di sessione.
 *
 *  - chiave  : scope + page + size + filtri normalizzati (ordine stabile)
 *  - TTL     : SEARCH_CACHE_TTL_MS (invalidazione temporale)
 *  - bypass  : opzione { force: true } sui thunk (refresh forzato)
 *  - purge   : clearSearchLavorabiliCache() dopo una mutazione che cambia
 *              l'insieme lavorabile (esitazione, presa in carico, ...)
 * ===================================================================== */
const SEARCH_CACHE_TTL_MS = 60000;
const searchLavorabiliCache = new Map();

const buildSearchCacheKey = (scope, page, size, filters = {}) => {
  const normalized = Object.fromEntries(
    Object.entries(filters)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b))
  );
  return JSON.stringify({ scope, page: page ?? null, size: size ?? null, filters: normalized });
};

const readSearchCache = (key) => {
  const hit = searchLavorabiliCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.ts > SEARCH_CACHE_TTL_MS) {
    searchLavorabiliCache.delete(key);   // entry scaduta
    return null;
  }
  return hit.data;
};

const writeSearchCache = (key, data) => {
  searchLavorabiliCache.set(key, { data, ts: Date.now() });
};

/**
 * Invalida l'intera cache delle ricerche lavorabili. Chiamare dopo una
 * mutazione che altera l'insieme dei task lavorabili (esitazione, presa in
 * carico, riassegnazione) affinche' la ricerca successiva ricarichi da BFF.
 * @function clearSearchLavorabiliCache
 */
export const clearSearchLavorabiliCache = () => {
  searchLavorabiliCache.clear();
};

/**
 * Get lista Ticket Lavorabili che, al return viene popolato ticketsLavorabili tramiteil metododo getTicketsLavorabili
 * @function fetchTicketsLavorabili
 * @param {number} [page] indice pagina 0-based (opzionale, abilita paginazione server-side)
 * @param {number} [size] dimensione pagina (opzionale, richiesto insieme a page)
 * @returns ritorna lista Ticket Lavorabili (o PagedTasksDTO se paginato)
*/
/**
 * Get lista task lavorabili COMPLETA (endpoint non paginato).
 *
 * <p>OSSQ-2381: prima della paginazione server-side (OSSQ-2332)
 * {@code ticketsLavorabili} conteneva l'insieme completo dei task lavorabili,
 * perche' il FE chiamava /task/lista-lavorabili senza page/size. Da allora
 * quello stato contiene solo la pagina a video (50 righe), ma i consumatori
 * che ragionavano sull'insieme completo sono rimasti invariati: inventario
 * riserve, valori dei filtri di colonna, ricerca duplicati. Risultato: liste
 * e filtri incompleti, senza alcun errore visibile.
 *
 * <p>Qui si ricarica l'insieme completo dallo STESSO endpoint di prima, in uno
 * stato separato e solo su richiesta dei punti che ne hanno bisogno: la
 * griglia principale resta paginata e il caricamento iniziale resta snello,
 * che era l'obiettivo di OSSQ-2332.
 *
 * @param {boolean} force ricarica anche se gia' in memoria
 */
export const fetchTicketsLavorabiliCompleti = (force = false) => async (dispatch, getState) => {
  const stato = getState()?.ticketReducer;
  if (!force && (stato?.lavorabiliCompletiLoaded || stato?.lavorabiliCompletiLoading)) {
    return stato?.ticketsLavorabiliCompleti ?? [];
  }

  dispatch(setLavorabiliCompletiLoading(true));
  try {
    // Nessun page/size: il BFF risponde con la lista intera, come pre-OSSQ-2332.
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-lavorabili`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    if (err || !res?.data) {
      console.warn('fetchTicketsLavorabiliCompleti failed', err);
      return [];
    }
    const lista = Array.isArray(res.data) ? res.data
                : Array.isArray(res.data?.content) ? res.data.content
                : [];
    dispatch(setTicketsLavorabiliCompleti(lista));
    return lista;
  } finally {
    dispatch(setLavorabiliCompletiLoading(false));
  }
};

export const fetchTicketsLavorabili = (page, size, filters = {}, force = false) => async (dispatch) => {
  // Pulisci i filtri vuoti (colonne server-supported passate dalla griglia).
  const cleanFilters = Object.fromEntries(
    Object.entries(filters || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const hasFilters = Object.keys(cleanFilters).length > 0;

  // Con filtri server-supported → endpoint /search (SP CContact light: filtra
  // DENTRO i lavorabili visibili all'utente). Senza → endpoint paginato
  // semplice. Entrambi popolano ticketsLavorabili + paginationLavorabili così
  // la griglia principale ha un'unica sorgente.
  const pageParams = (page !== undefined && page !== null && size) ? { page: String(page), size: String(size) } : {};
  const query = new URLSearchParams({ ...pageParams, ...cleanFilters }).toString();
  const path = hasFilters ? 'task/lista-lavorabili/search' : 'task/lista-lavorabili';
  const API_URI_CALL = `${API_URL}/api/v1/bff/${path}${query ? `?${query}` : ''}`;

  // Cache SOLO le fetch filtrate (= ricerche): il refresh non filtrato della
  // griglia resta sempre fresco. Applica il risultato in cache senza HTTP.
  const cacheKey = hasFilters ? buildSearchCacheKey('lavorabili', page ?? null, size ?? null, cleanFilters) : null;
  if (cacheKey && !force) {
    const cached = readSearchCache(cacheKey);
    if (cached) {
      if (cached && typeof cached === 'object' && Array.isArray(cached.content)) {
        dispatch(getTicketsLavorabili(cached.content));
        dispatch(setPaginationLavorabili({
          page: cached.page,
          size: cached.size,
          totalElements: cached.totalElements,
          totalPages: cached.totalPages,
        }));
      } else {
        dispatch(getTicketsLavorabili(cached));
      }
      return cached;
    }
  }

  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  if (err) console.error('fetchTicketsLavorabili error', err);
  if (!res || !res.data) return null;

  // Lazy-load per tab (WS-1): segnala che la lista lavorabili è caricata, così
  // il tab orchestrator non la ri-scarica alla riapertura. La paginazione da
  // onDataStateChange chiama comunque questo thunk direttamente (sempre fresca).
  dispatch(setListLoaded({ key: 'lavorabili', value: true }));

  // Payload paginato: { content, totalElements, page, size, totalPages }
  const isPaged = res.data && typeof res.data === 'object' && Array.isArray(res.data.content);
  if (isPaged) {
    dispatch(getTicketsLavorabili(res.data.content));
    dispatch(setPaginationLavorabili({
      page: res.data.page,
      size: res.data.size,
      totalElements: res.data.totalElements,
      totalPages: res.data.totalPages,
    }));
    if (cacheKey) writeSearchCache(cacheKey, res.data);
    return res.data;
  }

  if (cacheKey) writeSearchCache(cacheKey, res.data);
  return dispatch(getTicketsLavorabili(res.data));
};

/**
 * Ricerca paginata dei ticket lavorabili.
 *
 * Chiama l'endpoint BFF {@code GET /task/lista-lavorabili/search} con
 * paginazione + filtri opzionali. Sorgente unica CContact (SP light): la
 * visibilità e i dati sono identici all'as-is.
 *
 * Le ricerche già eseguite sono servite dalla cache lato client entro la
 * TTL (vedi {@link clearSearchLavorabiliCache}); passare {@code force:true}
 * per forzare il ricaricamento dal BFF.
 *
 * @param {Object} opts
 * @param {number} [opts.page=0]  indice pagina 0-based
 * @param {number} [opts.size=50] dimensione pagina
 * @param {Object} [opts.filters] mappa nome→valore dei filtri (idTask,
 *   nPolizza, sinistroId, contraente, numeroSinistroExternal,
 *   codiceDelegataria, numeroSinistroBroker, owner, canale, queue, priorita)
 * @param {boolean} [opts.force=false] bypassa la cache e ricarica dal BFF
 * @returns {Function} thunk che dispatch-a setSearchedTickets +
 *   setSearchPagination e ritorna il PagedTasksDTO o null.
 */
export const searchTicketsLavorabili = ({ page = 0, size = 50, filters = {}, force = false } = {}) => async (dispatch) => {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

  // Cache hit: stessa ricerca già eseguita di recente → riuso senza HTTP.
  const cacheKey = buildSearchCacheKey('search', page, size, cleanFilters);
  if (!force) {
    const cached = readSearchCache(cacheKey);
    if (cached) {
      const cachedList = Array.isArray(cached?.content) ? cached.content
                       : Array.isArray(cached)          ? cached
                       : [];
      dispatch(setSearchFilters(cleanFilters));
      dispatch(setSearchedTickets(cachedList));
      dispatch(setSearchPagination({
        page:          cached.page          ?? page,
        size:          cached.size          ?? size,
        totalElements: cached.totalElements ?? cachedList.length,
        totalPages:    cached.totalPages    ?? 1,
      }));
      return cached;
    }
  }

  const params = new URLSearchParams({ page: String(page), size: String(size), ...cleanFilters });
  const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-lavorabili/search?${params.toString()}`;

  dispatch(setSearchLoading(true));
  dispatch(setSearchFilters(cleanFilters));

  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);

  dispatch(setSearchLoading(false));

  if (err || !res?.data) {
    dispatch(setSearchedTickets([]));
    dispatch(setSearchPagination({ page, size, totalElements: 0, totalPages: 0 }));
    return null;
  }

  const payload = res.data;
  const list = Array.isArray(payload?.content) ? payload.content
             : Array.isArray(payload)          ? payload
             : [];
  dispatch(setSearchedTickets(list));
  dispatch(setSearchPagination({
    page:          payload.page          ?? page,
    size:          payload.size          ?? size,
    totalElements: payload.totalElements ?? list.length,
    totalPages:    payload.totalPages    ?? 1,
  }));
  writeSearchCache(cacheKey, payload);   // memo per ricerche successive identiche
  return payload;
};

/* -------- Selectors ricerca ibrida (spec §5.1) -------- */
export const selectSearchedTickets   = (state) => state?.ticketReducer?.searchedTickets   ?? [];
export const selectSearchPagination  = (state) => state?.ticketReducer?.searchPagination  ?? { page: 0, size: 50, totalElements: 0, totalPages: 0 };
export const selectSearchFilters     = (state) => state?.ticketReducer?.searchFilters     ?? {};
export const selectSearchLoading     = (state) => state?.ticketReducer?.searchLoading     ?? false;

/**
 * Get lista Ticket Lavorabili FULL (non paginata) — DEPRECATO.
 *
 * Rimpiazzato da {@link searchTicketsLavorabili} che fa ricerca server-side
 * paginata con filtri. Questo thunk resta per retrocompatibilità durante
 * il rollout in 3 fasi (spec §6) e sarà rimosso in Fase 3.
 * @deprecated Use `searchTicketsLavorabili({ page, size, filters })` instead.
 */
export const fetchTicketsLavorabiliFull = ({ force = false } = {}) => async (dispatch, getState) => {
  console.warn('[TicketSlice] fetchTicketsLavorabiliFull is deprecated — migrate to searchTicketsLavorabili');
  const state = getState()?.ticketReducer;
  const hasData = Array.isArray(state?.searchedTickets) && state.searchedTickets.length > 0;
  if (!force && hasData) {
    return state.searchedTickets;
  }
  // Reindirizza al nuovo thunk senza filtri per la retrocompatibilità.
  await dispatch(searchTicketsLavorabili({ page: 0, size: 500, filters: {} }));
  return getState()?.ticketReducer?.searchedTickets ?? [];
};

/**
 * Get lista Ticket Lavorabili che, al return viene popolato ticketsAssegnate tramite il metododo getTicketsAssegnate
 * @function fetchTicketsAssegnate
 * @returns ritorna lista Ticket Assegnati 
*/
export const fetchTicketsAssegnate = (force = false) => async (dispatch, getState) => {
  const { listsLoaded, listsLoading } = getState().ticketReducer;
  if (!force && (listsLoaded.assegnate || listsLoading.assegnate)) return null;
  dispatch(setListLoading({ key: 'assegnate', value: true }));
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-assegnate`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    if (err || !res?.data) return null;
    dispatch(getTicketsAssegnate(res.data));
    dispatch(setListLoaded({ key: 'assegnate', value: true }));
    return res.data;
  } finally {
    dispatch(setListLoading({ key: 'assegnate', value: false }));
  }
};

export const fetchTicketsScadenziario = (force = false) => async (dispatch, getState) => {
  const { listsLoaded, listsLoading } = getState().ticketReducer;
  if (!force && (listsLoaded.scadenziario || listsLoading.scadenziario)) return null;
  dispatch(setListLoading({ key: 'scadenziario', value: true }));
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-scadenze`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    if (err || !res?.data) return null;
    dispatch(getTicketsScadenziario(res.data));
    dispatch(setListLoaded({ key: 'scadenziario', value: true }));
    return res.data;
  } finally {
    dispatch(setListLoading({ key: 'scadenziario', value: false }));
  }
};

export const fetchTicketsScarti = (force = false) => async (dispatch, getState) => {
  const { listsLoaded, listsLoading } = getState().ticketReducer;
  if (!force && (listsLoaded.scarti || listsLoading.scarti)) return null;
  dispatch(setListLoading({ key: 'scarti', value: true }));
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-scarti`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    // 404/nessun dato => sezionale vuoto (comunque considerato "caricato")
    if (err || !res?.data) {
      dispatch(getTicketsScarti([]));
    } else {
      dispatch(getTicketsScarti(res.data));
    }
    dispatch(setListLoaded({ key: 'scarti', value: true }));
    return res?.data ?? [];
  } catch (error) {
    console.warn('fetchTicketsScarti failed', error);
    dispatch(getTicketsScarti([]));
    dispatch(setListLoaded({ key: 'scarti', value: true }));
    return [];
  } finally {
    dispatch(setListLoading({ key: 'scarti', value: false }));
  }
};

/**
 * CSP-3543: lista della tab 9 "Pagamenti da revisionare SAP" (esito SAP D/R).
 * Un errore del BFF lascia la vista vuota: la lista conta comunque come
 * caricata e si rilegge con Aggiorna, quando e' segnata sporca o dopo il TTL.
 */
export const fetchTicketsRevisioneSap = (force = false) => async (dispatch, getState) => {
  const { listsLoaded, listsLoading } = getState().ticketReducer;
  if (!force && (listsLoaded.revisioneSap || listsLoading.revisioneSap)) return null;
  dispatch(setListLoading({ key: 'revisioneSap', value: true }));
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-revisione-sap`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    let righe = res?.data;
    if (err || !righe) {
      console.warn('fetchTicketsRevisioneSap: nessun dato dal BFF', err);
      righe = [];
    }
    dispatch(getTicketsRevisioneSap(righe));
    dispatch(setListLoaded({ key: 'revisioneSap', value: true }));
    return righe;
  } finally {
    dispatch(setListLoading({ key: 'revisioneSap', value: false }));
  }
};

export const fetchTicketsChatUnread = () => async (dispatch) => {
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/lista-chat-unread`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res chat unread', res);
    console.log('err chat unread', err);

    if (err || !res?.data) {
      return null;
    }
    return dispatch(getTicketsChatUnread(res.data));
  } catch (error) {
    console.warn('fetchTicketsChatUnread failed', error);
    return null;
  }
};

export const fetchAllTicketsAssegnateLavorabili = (lavorabiliPage, lavorabiliSize) => async (dispatch, getState) => {
  dispatch(toogleLoadingGrid(true));

  try {
    // Server-side pagination per lavorabili: usa i parametri o lo stato corrente redux
    const currentPagination = getState()?.ticketReducer?.paginationLavorabili || { page: 0, size: 50 };
    const page = lavorabiliPage ?? currentPagination.page ?? 0;
    const size = lavorabiliSize ?? currentPagination.size ?? 50;

    await Promise.all([
      dispatch(fetchTicketsLavorabili(page, size, {}, true)),
      dispatch(fetchTicketsAssegnate(true)),
      dispatch(fetchTicketsScadenziario(true)),
      dispatch(fetchTicketsScarti(true))
    ]);

    try {
      await dispatch(fetchTicketsChatUnread());
    } catch (error) {
      console.warn('fetchTicketsChatUnread failed during grid refresh', error);
    }
  } finally {
    dispatch(toogleLoadingGrid(false));
  }
};

/**
 * Lazy-load per tab (WS-1). Carica SOLO le liste necessarie al tab indicato,
 * alla prima apertura (idempotente via flag listsLoaded/listsLoading) oppure
 * forzando il ricaricamento con {@code force:true}.
 *
 * Mappa tab → liste ticket:
 *  - '1' Scadenziario  → scadenziario
 *  - '2' I tuoi task   → assegnate
 *  - '3' Tutti i task  → lavorabili (paginata server-side)
 *  - '7' Inventario    → lavorabili (InventarioRiserve legge ticketsLavorabili)
 *  - '8' Scarti        → scarti
 *  - '9' Pagamenti da revisionare SAP → revisioneSap (CSP-3543)
 * Fuori da qui: '4' Agenda (fetchEvents, EventSlice), '5' Ricerca
 * (on-demand via searchTicketsLavorabili nel componente), '6' Eventi
 * Catastrofali (auto-fetch nel componente).
 */
const TAB_LIST_KEYS = {
  '1': ['scadenziario'],
  '2': ['assegnate'],
  '3': ['lavorabili'],
  '7': ['lavorabili'],
  '8': ['scarti'],
  '9': ['revisioneSap'],
};
const CHAT_UNREAD_KEYS = ['lavorabili', 'assegnate', 'scadenziario'];
// CSP-3428: oltre questa eta' una lista viene riletta all'apertura del tab
// anche se nessun evento l'ha segnata sporca.
const LIST_STALE_MS = 3 * 60 * 1000;

export const fetchTicketsByTab = (tab, force = false) => async (dispatch, getState) => {
  const keys = TAB_LIST_KEYS[tab];
  if (!keys || keys.length === 0) return;
  const { listsLoaded, listsLoading, listsDirty, listsFetchedAt } = getState().ticketReducer;
  // CSP-3428: si ricarica se la lista non c'e', se e' segnata sporca da
  // un'operazione andata a buon fine, o se e' piu' vecchia del TTL. Due
  // aperture ravvicinate dello stesso tab senza che sia successo nulla non
  // producono ancora nessuna chiamata: il vantaggio del caricamento per tab
  // resta. Il TTL copre i casi che nessun evento puo' segnalare, cioe' i
  // messaggi WebSocket persi (CSP-3429).
  const listaDaRileggere = (k) => {
    if (listsLoading[k]) return false;
    if (!listsLoaded[k]) return true;
    if (listsDirty?.[k]) return true;
    const ultimaLettura = listsFetchedAt?.[k] || 0;
    return (Date.now() - ultimaLettura) > LIST_STALE_MS;
  };
  const toLoad = force ? keys : keys.filter(listaDaRileggere);
  if (toLoad.length === 0) return;

  dispatch(toogleLoadingGrid(true));
  try {
    // force a true sulle singole thunk: la decisione di ricaricare e' gia'
    // stata presa qui sopra, e le thunk hanno una loro guardia su listsLoaded
    // che altrimenti farebbe uscire subito una lista solo sporca o scaduta.
    await Promise.all(toLoad.map((k) => {
      if (k === 'lavorabili') {
        const p = getState()?.ticketReducer?.paginationLavorabili || { page: 0, size: 50 };
        return dispatch(fetchTicketsLavorabili(p.page ?? 0, p.size ?? 50, {}, true));
      }
      if (k === 'assegnate') return dispatch(fetchTicketsAssegnate(true));
      if (k === 'scadenziario') return dispatch(fetchTicketsScadenziario(true));
      if (k === 'scarti') return dispatch(fetchTicketsScarti(true));
      if (k === 'revisioneSap') return dispatch(fetchTicketsRevisioneSap(true));
      return Promise.resolve();
    }));

    // Badge chat-unread solo se ho (ri)caricato una lista task che lo usa.
    if (toLoad.some((k) => CHAT_UNREAD_KEYS.includes(k))) {
      try {
        await dispatch(fetchTicketsChatUnread());
      } catch (error) {
        console.warn('fetchTicketsChatUnread failed during lazy load', error);
      }
    }
  } finally {
    dispatch(toogleLoadingGrid(false));
  }
};

/**
 * Refresh esplicito (pulsanti Aggiorna/Pulisci): invalida TUTTE le liste
 * ticket e ricarica SUBITO solo il tab attivo con force; le altre liste si
 * ricaricheranno alla prossima apertura del rispettivo tab.
 */
export const refreshActiveTab = (tab) => async (dispatch) => {
  dispatch(resetTicketListsLoaded());
  await dispatch(fetchTicketsByTab(tab, true));
};

/**
 * Get Dettaglio Ticket, contiene tutti i dati neccesari alla compialzione del task. E viene salvato nel nodo openedTicket e al nodo allOpenedTicket / In caso di Cache Attivo viene usato il metodo addOpenedTicketCached
 * @function fetchTicket
 * @param {String} taskId id del task
*/
export const fetchTicket = (taskId) => async (dispatch) => {
  dispatch(toogleLoading(true));
  dispatch(getTicket({}));
  dispatch(setCheckWorldStatus({
    checked: false,
    status: '',
    gridValues: [],
    dataEsecuzione: null
  }));

  if (taskId?.cache === false || taskId?.cache === undefined) {
    //const fetchData = async () => axiosInstance.get('/api/data/singleTicket/singleTicketData'); taskId.taskId
    const API_URI_CALL = `${API_URL}/api/v1/bff/task/dettaglio/${taskId.taskId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    dispatch(addOpenedTicket(res.data));
    dispatch(getTicket(res.data));
    const ObjectTask = {
      taskId: taskId?.taskId,
      nSinistro: res?.data?.datiDenuncia?.nSinistro,
    };

    dispatch(setStatoRichiestaAnticipo(res?.data?.datiDenuncia?.statoRichiestaAnticipo));

    dispatch(addTaskToNavSinstro(ObjectTask));
  } else {
    setTimeout(() => {
      const datiTask = { "idTask": taskId.taskId };
      dispatch(addOpenedTicketCached({ datiTask }));
    }, 1000);
  }

  return dispatch(toogleLoading(false));
};

/**
 * Polla in modo SILENZIOSO il dettaglio del task finché esce dallo stato PREASSEGNAZIONE
 * (oppure scadono i tentativi). Usato dopo la creazione di una nuova Causa: il task appena
 * creato resta in coda PREASSEGNAZIONE finché CCONTACT non assegna la coda definitiva, e
 * fino ad allora la lista esiti è ridotta (manca "Causa aperta").
 *
 * A differenza di fetchTicket NON mostra lo spinner globale e NON azzera openedTicket: legge
 * il dettaglio in background e committa lo store UNA SOLA VOLTA, quando la coda risulta
 * assegnata, aggiornando SOLO datiTask (setOpenedTicketDatiTask). L'aggiornamento di
 * datiTask.queue_id fa ripartire l'effetto che ricarica gli esiti (loadSpotsEsiti), senza
 * flicker della pagina e senza azzerare i nodi caricati a parte come listaDanneggiati.
 * @param {String} taskId id del task in PREASSEGNAZIONE
 * @param {{maxAttempts?: number, intervalMs?: number}} [options] tentativi e intervallo poll
 */
export const pollTicketUntilAssigned = (taskId, options = {}) => async (dispatch, getState) => {
  const { maxAttempts = 10, intervalMs = 1500 } = options;
  if (!taskId) return;

  const isPreassegnazione = (value) => typeof value === 'string' && value.toUpperCase().includes('PREASSEGNAZIONE');
  const openedDatiTask = () => getState()?.ticketReducer?.openedTicket?.datiTask;
  const isStillCurrentTask = () => {
    const dt = openedDatiTask();
    return Boolean(dt) && String(dt.idTask) === String(taskId);
  };

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    // L'utente ha cambiato task, oppure la coda è già stata assegnata: stop.
    if (!isStillCurrentTask()) return;
    const dt = openedDatiTask();
    if (!isPreassegnazione(dt?.tipologia) && !isPreassegnazione(dt?.queue_desc)) return;

    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, intervalMs); });
    if (!isStillCurrentTask()) return;

    const API_URI_CALL = `${API_URL}/api/v1/bff/task/dettaglio/${taskId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    // eslint-disable-next-line no-await-in-loop
    const [res] = await executor.executeAsync(fetchData);

    const newDatiTask = res?.data?.datiTask;
    if (newDatiTask) {
      if (!isStillCurrentTask()) return;
      const assegnato = !isPreassegnazione(newDatiTask.tipologia) && !isPreassegnazione(newDatiTask.queue_desc);
      if (assegnato) {
        // Commit mirato: aggiorna SOLO datiTask (e quindi queueId) senza ricostruire
        // openedTicket, così i nodi caricati a parte (es. listaDanneggiati) sopravvivono
        // al poll e la select del danneggiato non si svuota (CSP-2964).
        dispatch(setOpenedTicketDatiTask(newDatiTask));
        return;
      }
    }
  }
};

/**
 * Get lista tipologie email per groupingId, salvate in emailTipologie tramite setEmailTipologie.
 * Non piu usata dal tab email: la tipologia non e esposta in UI (CSP-2636), resta solo sul
 * contratto di salvataggio dove viene rimandata invariata.
 * @function fetchEmailTipologie
 * @param {String|Number} groupingId identificativo del gruppo di email
 * @returns setEmailTipologie con la lista delle tipologie
 */
export const fetchEmailTipologie = (groupingId) => async (dispatch) => {
  if (!groupingId) {
    return null;
  }

  const API_URI_CALL = `${API_URL}/api/v1/bff/email/lista/tipologie/${groupingId}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);

  return dispatch(setEmailTipologie(res?.data || []));
};

/**
 * Post tipologia e descrizione di una email per emailId.
 * La descrizione è un testo libero usato per catalogare la mail quando il solo oggetto
 * non è sufficiente (CSP-2636); viene inviata sempre, anche vuota, per consentirne la cancellazione.
 * @function postTipologiaEmail
 * @param {String} emailId ID dell'email
 * @param {Number} inboxOutBox 0 per inbox, 1 per outbox
 * @param {String} idTipologia ID della tipologia
 * @param {String} descrizione Descrizione libera dell'email
 * @returns {Boolean} true se il salvataggio è andato a buon fine
 */
export const postTipologiaEmail = (emailId, inboxOutBox, idTipologia, descrizione) => async () => {

  const API_URI_CALL = `${API_URL}/api/v1/bff/email/tipologia/${emailId}`;
  const fetchData = async () => axiosInstance.post(API_URI_CALL, {
    "inbox": inboxOutBox,
    "typeId": idTipologia,
    "descrizione": descrizione ?? ''
  });
  const [, err] = await executor.executeAsync(fetchData);

  return !err;
};

/**
 * Get Dettaglio Ticket, contiene tutti i dati neccesari alla compialzione del task. In questo caso salviamo la response nel metodo getTicketTree che popola il nodo datiTaskFigli per l'abero laterale
 * @function fetchTicketTree
 * @param {String} taskId ID del task
 * @returns getTicketTree dati albero laterale
*/
export const fetchTicketTree = (taskId) => async (dispatch) => {
  // dispatch(toogleLoading());

  //const fetchData = async () => axiosInstance.get('/api/data/singleTicket/singleTicketData'); taskId.taskId
  const API_URI_CALL = `${API_URL}/api/v1/bff/task/dettaglio/${taskId.taskId}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);

  return dispatch(getTicketTree(res.data));

  // return dispatch(toogleLoading());
};

export const rebuildModTicket = (taskId) => async (dispatch) => {
  dispatch(toogleLoading(true));
  dispatch(getTicket({}));
  dispatch(setCheckWorldStatus({
    checked: false,
    status: '',
    gridValues: [],
    dataEsecuzione: null
  }));

  //const fetchData = async () => axiosInstance.get('/api/data/singleTicket/singleTicketData'); taskId.taskId
  const API_URI_CALL = `${API_URL}/api/v1/bff/task/dettaglio/${taskId.taskId}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);


  dispatch(setOpenedTicketChangePolizza(res.data));
  dispatch(getTicket(res.data));

  // dispatch(resetModifiedTicket());
  return dispatch(toogleLoading(false));
};

/**
 * Get Lista Danneggiati, i dati della response tramite il metodo getListaDanneggiati vengono salvati nel nodo listaDanneggiati, viene anche salvato il ramo completo tramite setRamoGaranziaDanneggiato nel nodo ramoGaranziaDanneggiato e il nodo con due cifre tramite il metodo setRamoGaranzia nel nodo ramoGaranziaPut
 * @function fetchListaDanneggiati
 * @param {uuID} idSinistro ID sinistro
 * @returns getListaDanneggiati lista Danneggiati
*/
export const fetchListaDanneggiati = (idSinistro) => async (dispatch) => {
  if (idSinistro) {
    //dispatch(toogleLoading());
    //dispatch(toggleIsButtonEnabledMassimaleGaranzieVisible(false));
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/lista/danneggiati/${idSinistro}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    if (res?.data?.length > 0) {
      //ramoGaranziaDanneggiato:"0013"
      dispatch(setRamoGaranziaDanneggiato(res.data[0].ramoGaranzia));
      //Salvo nel momento in cui mi arrivano i dati dal BE un oggetto che contiene il singolo idDanneggiato con la relativa valuta in modo da averlo in redux
      for (let i = 0; i < res.data.length; i++) {
        dispatch(setSelectedMultivaluta({
          idDanneggiato: res.data[i]?.id,
          valuta: res.data[i]?.valutaOperazione
        }))
      }


      const inputValue = res?.data[0]?.ramoGaranzia;
      if (inputValue != null) {
        let outputValue = '';
        //Prendo ultime due cifre del ramo
        if (parseInt(inputValue, 10) < 10) {
          outputValue = inputValue.slice(-2);
        } else {
          for (let i = 0; i < inputValue.length; i++) {
            const digit = parseInt(inputValue[i], 10);
            if (digit > 0) {
              outputValue += digit;
            }
          }
        }
        //ramoGaranziaPut:"13"
        dispatch(setRamoGaranzia(outputValue));
      }

    }
    // On success store the array. On error do NOT overwrite the existing list: the original
    // code threw on `res.data` before dispatching, so consumers kept their previously loaded
    // danneggiati; we preserve that (no clobber) while removing the crash. The DatiIncarico
    // re-fetch guard stays loop-safe because on error `listaDanneggiati` keeps its value, so the
    // effect dependency does not change and no refetch loop occurs.
    if (Array.isArray(res?.data)) {
      return dispatch(getListaDanneggiati(res.data));
    }
    return null;
    //dispatch(toogleLoading());
    //return dispatch(toggleIsButtonEnabledMassimaleGaranzieVisible(true));
  }
  //return dispatch(toggleIsButtonEnabledMassimaleGaranzieVisible(true));
  return null;
};

/**
 * Ritorna la lista dei beneficiari 
 * @param {String} nPolizza 
 * @returns lista beneficiari pagamento indennizzo
*/
export const fetchListaBeneficiarioLqInd = (nPolizza, sinistroId) => async (dispatch, getState) => {
  if (nPolizza !== "" && nPolizza !== null && nPolizza !== undefined) {
    // CSP-3098: per le collettive OverX il BFF richiede il numeroAdesione al posto del numero polizza master.
    // Per le individuali numeroAdesione è null e si usa nPolizza come prima (path invariato).
    const numeroAdesione = getState().ticketReducer?.openedTicket?.datiPolizza?.numeroAdesione;
    const identificativoPolizza = numeroAdesione ? encodeURIComponent(numeroAdesione) : nPolizza;
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/beneficiario-indennizzo/${sinistroId}/${identificativoPolizza}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res?.data?.length > 0) {
      return dispatch(getListaBeneficiarioLqInd(res.data))
    }
  }
  return null
}

/**
 * Get Lista Rmi che popola la selec Rami tramite il metodo getRamiSinistro e popola il nodo listaRamiSinistro
 * @function fetchListaRamiSinistro
 * @param {String} nPolizza Numero Polizza
 * @param {String} idSinistro ID Sinistro
 * @param {String} idTitoloAmministrativo ID Titolo amministrativo
 * @param {String} idBene ID Bene
 * @returns getRamiSinistro
*/
export const fetchListaRamiSinistro = (nPolizza, idSinistro, idTitoloAmministrativo, idBene) => async (dispatch) => {
  const controlloForIdAmm = "0";
  // eslint-disable-next-line eqeqeq
  if (nPolizza && nPolizza !== 'N/A' && idSinistro && ((idTitoloAmministrativo && idTitoloAmministrativo != (controlloForIdAmm)) || idBene)) {
    let queryParamPolizza;
    // OSSQ-2323: il BFF (checkIdTitoloBeneJmil) ammette ESATTAMENTE uno tra idTitolo e idBene; per le
    // collettive (OXC) il path /rami usa solo idTitolo (datoPo) e ignora idBene. Diamo quindi precedenza
    // a idTitolo quando valido (es. idTitoloGetRami passato da AdminDataCollettive), altrimenti idBene.
    // Prima: nel ramo idBene si costruiva "?idBene=...&?idTitolo=..." (con un '?' di troppo): l'idTitolo
    // arrivava malformato/null al BFF e per le collettive la lista rami tornava vuota.
    // eslint-disable-next-line eqeqeq
    if (idTitoloAmministrativo && idTitoloAmministrativo != controlloForIdAmm) {
      queryParamPolizza = `?idTitolo=${idTitoloAmministrativo}`
    } else {
      queryParamPolizza = `?idBene=${idBene}`
    }
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/rami/${nPolizza}/${idSinistro}${queryParamPolizza}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getRamiSinistro(res.data));
  }
  return null;
};

/**
 * CSP-3170 - Get DDL dei motivi di chiusura senza seguito, salva in state.motiviSenzaSeguito.
 * @function fetchMotiviSenzaSeguito
 * @returns setMotiviSenzaSeguito con la lista dei motivi
 */
export const fetchMotiviSenzaSeguito = (force = false) => async (dispatch, getState) => {
  // CSP-3170: cache in-memory - i motivi cambiano di rado, evitiamo la chiamata ad ogni apertura del modale.
  const cached = getState()?.ticketReducer?.motiviSenzaSeguito;
  if (!force && Array.isArray(cached) && cached.length > 0) {
    return cached;
  }
  const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/motivi-senza-seguito`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);
  if (err) {
    console.log('fetchMotiviSenzaSeguito err', err);
    return dispatch(setMotiviSenzaSeguito([]));
  }
  return dispatch(setMotiviSenzaSeguito(res.data));
};

/**
 * CSP-3170 - Get catena clonazioni: risale i sinistri origine a partire dal nDenuncia corrente.
 * Popola state.catenaClonazioni (lista di { idTask, numeroSinistro, nDenuncia }).
 * @function fetchCatenaClonazioni
 * @param {String} nDenuncia numero denuncia del sinistro corrente (clonato)
 * @returns setCatenaClonazioni con la lista degli anelli della catena
 */
export const fetchCatenaClonazioni = (nDenuncia) => async (dispatch) => {
  if (!nDenuncia) {
    return dispatch(setCatenaClonazioni([]));
  }
  const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/catena-clonazioni/${nDenuncia}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);
  if (err) {
    console.log('fetchCatenaClonazioni err', err);
    return dispatch(setCatenaClonazioni([]));
  }
  return dispatch(setCatenaClonazioni(res.data));
};

/**
 * Get Lista Garanzie per Danneggiato, ritorna le garanzie(prestazioni) tramite il metodo getGaranzieDanneggiato che salverà i dati nel nodo listaGaranzieDanneggiato. In base all idBene verrà utilizzato un Paramquery diverso idBene=${idBene} o idTitolo=${idTitoloJMIL}
 * @function fetchListaGaranzieDanneggiato
 * @param {String} taskId ID del task
 * @param {String} danneggiatoId ID del Danneggiato
 * @param {String} codiceRamo codice del ramo
 * @param {String} idTitoloJMIL ID Titlo amministrativo JMIL
 * @param {String} idBene ID del Bene
 * @returns getGaranzieDanneggiato ritorna lista garanzie
*/
export const fetchListaGaranzieDanneggiato = (taskId, danneggiatoId, codiceRamo, idTitoloJMIL, idBene) => async (dispatch, getState) => {

  if (taskId && danneggiatoId && codiceRamo) {
    let queryParamPolizza;
    if (idBene) {
      queryParamPolizza = `idBene=${idBene}`

    } else {
      queryParamPolizza = `idTitolo=${idTitoloJMIL}`
    }
    //dispatch(toogleLoading());
    /* Da smontare e rifare correttamente */
    const onlyDigits = parseInt(codiceRamo, 10); // Rimuovo tutte le lettere dalla stringa

    const newStringCodiceRamo = `00${onlyDigits.toString()}`;

    const subStringRamo = newStringCodiceRamo.slice(-2);

    // CSP-3098: per le collettive OverX il BFF richiede il numeroAdesione (es. OXC0001-000001).
    // Il dato è già nel dettaglio task (datiPolizza); per le polizze individuali è null e l'URL resta invariato.
    const numeroAdesione = getState().ticketReducer?.openedTicket?.datiPolizza?.numeroAdesione;
    const queryParamAdesione = numeroAdesione ? `&numeroAdesione=${encodeURIComponent(numeroAdesione)}` : '';

    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/garanzie/${taskId}/${danneggiatoId}?codiceRamo=${subStringRamo}&${queryParamPolizza}${queryParamAdesione}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    dispatch(getGaranzieDanneggiato(res.data));
    // dispatch(toogleLoading());
    return dispatch(toggleIsButtonEnabledMassimaleGaranzieVisible(true));
  }
  return null;
};

/**
 * Get Lista Precedenti Sinistri, tramite il metodo getPrecedentiSinistri i dati vengono dalvati nel nodo precedentiSinistri per popolare la tabella Precendenti Sinistri
 * @function fetchPrecendentiSinistri
 * @param {String} nPolizza Numero Polizza
 * @param {String} idSinistro ID Sinistro
 * @returns getPrecedentiSinistri popola la tabella Precendenti Sinistri
*/
export const fetchPrecendentiSinistri = (nPolizza, idSinistro) => async (dispatch) => {
  if (nPolizza && nPolizza !== 'N/A' && idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/lista/sinistri-precedenti/${nPolizza}/id/${idSinistro}`;
    //this is shit
    const uuID = crypto.randomUUID();
    const accessToken = localStorage.getItem('access_token');
    const idFlussoLocal = localStorage.getItem('idFlusso');

    if (accessToken) {
      axiosInstance.defaults.headers.common = { Authorization: `Bearer ${accessToken}`, requestId: uuID, page: 0, size: 100, idFlusso: idFlussoLocal };
    }
    //this is shit
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getPrecedentiSinistri(res.data));
  }
  return null;
};

/**
 * Get Riserve complessive, tramite il metodo getRiserveComplessive viene popolato il nodo riserveComplessive, per popolare il componente delle riserve complessive
 * @function fetchRiserveComplessive
 * @param {String} idSinistro ID Sinistro
 * @returns getRiserveComplessive popolare risreve complessive
*/

export const fetchRiserveComplessive = (idSinistro, quota) => async (dispatch) => {
  // OSSQ-2386: `quota` e' una percentuale 0-100 (os-sinistri la divide per 100). Lo zero e' un
  // valore legittimo in portafoglio: con il test di verita' la chiamata veniva saltata in silenzio.
  if (idSinistro && quota !== undefined && quota !== null && !Number.isNaN(Number(quota))) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/lista/riserve-complessive/${idSinistro}`;
    //this is shit
    const accessToken = localStorage.getItem('access_token');

    if (accessToken) {
      axiosInstance.defaults.headers.common = { Authorization: `Bearer ${accessToken}`, quota }; //quota da capire se deve essere dinamico
    }
    //this is shit
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err getRiserveComplessive', err);

    return dispatch(getRiserveComplessive(res.data));
  }
  return null;
};

/**
 * Get Lista Cause Istruttoria, tramite il metodo getCauseIstruttoria viene popolato il nodo causeIstruttoria, per popolare la tabella cause collegate al sinistro
 * @function fetchCauseIstruttoria
 * @param {String} idSinistro ID Sinistro
 * @returns getCauseIstruttoria popola tabella CauseIstruttoria
*/
export const fetchCauseIstruttoria = (idSinistro) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/lista/${idSinistro}?size=100&page=0`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getCauseIstruttoria(res.data));
  }
  return null;
};

/**
 * Get Lista Soggetti Causa, tramite il metodo getListaSoggettiCause, viene popolato il nodo listaSoggetti, serve per popolare la select dei possibili soggetti coinvoliti in una casua nel task Cause
 * @function fetchListaSoggettiCausa
 * @param {String} causaId ID Causa
 * @returns getListaSoggettiCause popola select Soggetti Causa
*/
export const fetchListaSoggettiCausa = (causaId) => async (dispatch) => {
  if (causaId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/soggetti/${causaId}?size=20&page=0`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getListaSoggettiCause(res.data));
  }
  return null;
};

/**
 * Get Lista Incarichi in Istruttoria, tramite il metodo getIncarichiIstruttoria viene popolato il nodo incarichiIstruttoria, popola tabella Incarichi in Istruttoria
 * @function fetchIncarichiIstruttoria
 * @param {String} idSinistro ID Sinistro
 * @returns getIncarichiIstruttoria popola tabella Incarichi in Istruttoria
*/
export const fetchIncarichiIstruttoria = (idSinistro, idPagamentoSpesa) => async (dispatch) => {
  if (idPagamentoSpesa === undefined || idPagamentoSpesa === null) {
    idPagamentoSpesa = "null";
  }
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/lista/${idSinistro}?idPagamentoSpesa=${idPagamentoSpesa}&size=100&page=0`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    console.log('res', res);

    for (let i = 0; i < res.data?.content?.length; i++) {
      dispatch(setSelectedMultivalutaIncarico({
        idIncarico: res.data?.content[i]?.id,
        valuta: res.data?.content[i]?.valutaOperazione,
        tassoCambioEur: res.data?.content[i]?.tassoCambioEur,
        disableButton: true
      }))
    }

    return dispatch(getIncarichiIstruttoria(res.data));
  }
  return null;
};

/**
 * Get Lista Incarichi in Quadro, tramite il metodo getListaIncaricatiInQuadro viene popolato il nodo listaIncaricatiInQuadro, nel task TIpo Incarico quando viene selezionata una specilizzazione viene popolata la tabella degli incaricati in quadro.
 * @function fetchListaIncarichiInQuadro
 * @param {String} specializzazzione Tipo specilizzazione
 * @returns getListaIncaricatiInQuadro popola tabella Incaricati in quadro
*/
export const fetchListaIncarichiInQuadro = (specializzazzione) => async (dispatch) => {
  if (specializzazzione) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/incaricati/${specializzazzione}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getListaIncaricatiInQuadro(res.data));
  }
  return null;
};

/**
 * Costruisce e valida i query params per la ricerca fiduciari unificata.
 *
 * Contesti supportati:
 * - Portal (`suPortale = SI`): richiede `cap`, `tipoPerizia`, aggiunge `isPortal=true`.
 * - OXS (`suPortale = NO`): richiede `tipoFiduciario`, `competenza`, `areaGeograficaCompetenza`,
 *   con `specializzazione` opzionale.
 *
 * @param {Object} filters filtri provenienti dalla UI.
 * @returns {{params: URLSearchParams, suPortale: 'SI'|'NO'} | {error: string}}
 */
const buildRicercaFiduciariQuery = (filters = {}) => {
  const suPortale = resolveSearchContext(filters?.suPortale);
  const params = new URLSearchParams();

  if (suPortale === SEARCH_CONTEXT.PORTAL) {
    const cap = safeString(filters.cap);
    const tipoPerizia = safeString(filters.tipoPerizia);
    if (!cap || !tipoPerizia) {
      return { error: FIDUCIARI_SEARCH_ERROR.MISSING_PORTAL_REQUIRED };
    }
    params.set(FIDUCIARI_QUERY_PARAM.IS_PORTAL, 'true');
    params.set(FIDUCIARI_QUERY_PARAM.CAP, cap);
    params.set(FIDUCIARI_QUERY_PARAM.TIPO_PERIZIA, tipoPerizia);
    return { params, suPortale };
  }

  const tipoFiduciario = safeString(filters.tipoFiduciario || filters.tipoIncarico);
  const competenza = safeString(filters.competenza);
  const specializzazione = safeString(filters.specializzazione);
  const areaGeograficaCompetenza = safeString(filters.areaGeograficaCompetenza || filters.luogoCompetenzaGeografica).toUpperCase();
  const areaGeograficaId = safeString(filters.areaGeograficaId);

  if (!tipoFiduciario || !competenza || !areaGeograficaCompetenza) {
    return {
      error: FIDUCIARI_SEARCH_ERROR.MISSING_OXS_REQUIRED,
    };
  }
  if (!AREA_GEOGRAFICA_ALLOWED.has(areaGeograficaCompetenza)) {
    return { error: FIDUCIARI_SEARCH_ERROR.INVALID_OXS_AREA };
  }
  if (['NAZIONALE', 'REGIONALE', 'PROVINCIALE'].includes(areaGeograficaCompetenza) && !areaGeograficaId) {
    return { error: FIDUCIARI_SEARCH_ERROR.MISSING_OXS_AREA_ID };
  }

  params.set(FIDUCIARI_QUERY_PARAM.TIPO_FIDUCIARIO, tipoFiduciario);
  params.set(FIDUCIARI_QUERY_PARAM.COMPETENZA, competenza);
  params.set(FIDUCIARI_QUERY_PARAM.AREA_GEOGRAFICA_COMPETENZA, areaGeograficaCompetenza);
  if (specializzazione) params.set(FIDUCIARI_QUERY_PARAM.SPECIALIZZAZIONE, specializzazione);
  if (['NAZIONALE', 'REGIONALE', 'PROVINCIALE'].includes(areaGeograficaCompetenza) && areaGeograficaId) {
    params.set(FIDUCIARI_QUERY_PARAM.AREA_GEOGRAFICA_ID, areaGeograficaId);
  }

  return { params, suPortale };
};

/**
 * Esegue la ricerca fiduciari usando endpoint unificato BFF.
 * Endpoint: GET /api/v1/bff/fiduciario/ricerca
 *
 * Effetti redux:
 * - toggle loading/error ricerca
 * - normalizzazione payload BE
 * - aggiornamento liste incaricati in quadro/no quadro
 *
 * @param {Object} filters filtri UI (portal/oxs).
 * @returns {Promise<Array|null>} lista normalizzata o null in errore.
 */
export const fetchRicercaFiduciari = (filters) => async (dispatch) => {
  dispatch(setIncaricatiSearchLoading(true));
  dispatch(setIncaricatiSearchError(null));
  try {
    const { params, suPortale, error } = buildRicercaFiduciariQuery(filters || {});
    if (error) {
      dispatch(setIncaricatiSearchError(error));
      return null;
    }

    const API_URI_CALL = `${API_URL}/api/v1/bff/fiduciario/ricerca?${params.toString()}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      const normalized = normalizeRicercaFiduciariResponse(res, suPortale);
      dispatch(getListaIncaricatoNoQuadro(normalized));
      dispatch(getListaIncaricatiInQuadro(normalized));
      return normalized;
    }

    console.log('err', err);
    dispatch(setIncaricatiSearchError(FIDUCIARI_SEARCH_ERROR.SEARCH_FAILED));
    return null;
  } catch (error) {
    dispatch(setIncaricatiSearchError(FIDUCIARI_SEARCH_ERROR.SEARCH_FAILED));
    return null;
  } finally {
    dispatch(setIncaricatiSearchLoading(false));
  }
};

/**
 * Alias pubblico per retro-compatibilità lato componenti.
 * Canonico: delega sempre a `fetchRicercaFiduciari`.
 *
 * @param {Object} filters filtri di ricerca.
 * @returns {Promise<Array|null>}
 */
export const fetchSearchIncaricati = (filters) => async (dispatch) => dispatch(fetchRicercaFiduciari(filters));

/**
 * Recupera le aree geografiche per i filtri OXS del task incarico.
 * Endpoint: GET /api/v1/bff/fiduciario/area-geografica
 *
 * @param {'REGIONE'|'PROVINCIA'} tipoArea tipo area richiesto dal BFF.
 * @returns {Promise<Array|null>}
 */
export const fetchAreaGeografica = (tipoArea) => async (dispatch) => {
  const normalizedTipoArea = safeString(tipoArea).toUpperCase();
  const key = normalizedTipoArea === 'NAZIONE'
    ? 'nazioni'
    : normalizedTipoArea === 'REGIONE'
      ? 'regioni'
      : normalizedTipoArea === 'PROVINCIA'
        ? 'province'
        : null;

  if (!key) {
    return null;
  }

  dispatch(setAreaGeograficaLoading({ key, loading: true }));

  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/fiduciario/area-geografica?tipoArea=${normalizedTipoArea}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res] = await executor.executeAsync(fetchData);
    const items = res?.data?.content || [];
    dispatch(setAreaGeograficaOptions({ key, items }));
    return items;
  } catch (error) {
    dispatch(setAreaGeograficaOptions({ key, items: [] }));
    return null;
  } finally {
    dispatch(setAreaGeograficaLoading({ key, loading: false }));
  }
};


/**
 * Recupera il dettaglio di un fiduciario no-quadro per ID.
 * Endpoint: GET /api/v1/bff/incarichi/incaricato/{incaricatoId}
 *
 * Usato dai flussi toast post-create/post-update incaricato.
 *
 * @function fetchListaIncaricatoNoQuadro
 * @param {String} incaricatoId ID Incaricato
 * @returns getListaIncaricatoNoQuadro popola tabella Incaricati No quadro
*/
export const fetchListaIncaricatoNoQuadro = (incaricatoId) => async (dispatch) => {
  if (incaricatoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/incaricato/${incaricatoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    const normalized = Array.isArray(res?.data)
      ? res.data
      : (res?.data ? [res.data] : []);
    return dispatch(getListaIncaricatoNoQuadro(normalized));
  }
  return null;
};

/**
 * Ricerca legacy no-quadro con path params multipli.
 * Endpoint: GET /api/v1/bff/incarichi/incaricati/{...}
 *
 * @deprecated Usare `fetchSearchIncaricati` (endpoint unico /fiduciario/ricerca).
 */
export const fetchListaIncaricatoNoQuadroByFiltroLegacy = (
  tipoFiduciarioId,
  compentenzaId,
  specializzazione,
  sinistroIngente,
  sinistroFrequenza,
  accordoQuadro,
  attivo,
) => async (dispatch) => {
  if (tipoFiduciarioId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/incaricati/${tipoFiduciarioId}/${compentenzaId}/${specializzazione}/${sinistroIngente}/${sinistroFrequenza}/${accordoQuadro}/${attivo}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    const normalized = Array.isArray(res?.data)
      ? res.data
      : (res?.data ? [res.data] : []);
    return dispatch(getListaIncaricatoNoQuadro(normalized));
  }
  return null;
};

/**
 * Get Riserva Spesa Incaricato, tramite il metodo getRiservaSpeseIncarico viene popolato il nodo riservaSpeseIncarico, si visualizza la riserva riserva all'incaricato scelto nel gtask incarichi
 * @function fetchRiserveSpeseIncarico
 * @param {String} riservaSpesaId ID Riserva Spesa
 * @returns getRiservaSpeseIncarico popola component Riserva Incarico
*/
export const fetchRiserveSpeseIncarico = (riservaSpesaId) => async (dispatch) => {
  if (riservaSpesaId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/riserva-spesa/${riservaSpesaId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getRiservaSpeseIncarico(res.data));
  }
  return null;
};

/**
 * Get Lista Validazioni, tramite il metodo getValidazioniIstruttoria viene popolato il nodo validazioniIstruttoria, popola tabella validazioni in istruttoria
 * fetchValidazioniIstruttoria
 * @param {String} idSinistro ID Sinistro
 * @returns getValidazioniIstruttoria popola tabella validazioni in istruttoria
*/
export const fetchValidazioniIstruttoria = (idSinistro) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/validazioni/lista/${idSinistro}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getValidazioniIstruttoria(res.data));
  }
  return null;
};

/**
 * Recupera la configurazione filtri fiduciario per bootstrap UI.
 * Endpoint: GET /api/v1/bff/fiduciario/configurazioni
 * Response attesa: albero tipi fiduciario + tipi incarico.
 *
 * @function fetchConfigTipiIncarico
 * @returns setFiduciariConfig popola ticketReducer.fiduciariConfig
 */
export const fetchConfigTipiIncarico = () => async (dispatch) => {
  const API_URI_CALL = `${API_URL}/api/v1/bff/fiduciario/configurazioni`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  if (res) {
    return dispatch(setFiduciariConfig(res.data));
  }

  console.log(err)
  return null
};

/**
 * Trova nuova polizza, tramite il metodo getListaSearchPolizza viene popolato il nodo listaSearchPolizza, popola tabella con la lista delle possibili polizze.
 * @function fetchSearchPolizza
 * @param {String} polizza Numero polizza
 * @returns getListaSearchPolizza trova polizza
*/
export const fetchSearchPolizza = (polizza) => async (dispatch) => {
  dispatch(getListaSearchPolizzaCollettiva([]));
  const API_URI_CALL = `${API_URL}/api/v1/bff/overx/polizze/generica/find/${polizza}?limit=10`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);
  if (res.data) {
    return dispatch(getListaSearchPolizza(res.data));
  }
  return 0;
};

/**
 * Get Search Polizza Collettiva, tramite il metodo getListaSearchPolizzaCollettiva viene popolato il nodo listaSearchPolizzaCollettiva
 * @function fetchSearchPolizzaCollettiva
 * @param {String} polizza Numero Polizza
 * @returns 
*/
export const fetchSearchPolizzaCollettiva = (polizza) => async (dispatch) => {
  dispatch(getListaSearchPolizza([]));
  const API_URI_CALL = `${API_URL}/api/v1/bff/beneCollettivo/numeroPolizza/${polizza}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);
  if (res.data) {
    return dispatch(getListaSearchPolizzaCollettiva(res.data));
  }
  return 0;
};


/**
 * Get Search Polizza Collettiva, tramite il metodo fetchSearchDatiBeniCollettivi viene popolato il nodo datiAmministrativi per le collettive
 * @function fetchSearchDatiBeniCollettivi
 * @param {String} polizza Numero Polizza
 * @returns 
*/
export const fetchSearchDatiBeniCollettivi = (polizza) => async (dispatch) => {
  dispatch(getListaSearchPolizzaCollettiva([]));
  const API_URI_CALL = `${API_URL}/api/v1/bff/beneCollettivo/numeroPolizza/${polizza}`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);
  if (res.data) {
    return dispatch(getListaBeniCollettiviAmministrativi(res.data));
  }
  return 0;
};

/**
 * Chiamata per descrizione bene colletivo, tramite il metodo getListaSearchCollettiva viene popolato il nodo listaSearchCollettiva
 * @function fetchSearchCollettiva
 * @param {String} descrizioneBene
 * @returns getListaSearchCollettiva
*/
export const fetchSearchCollettiva = (descrizioneBene) => async (dispatch) => {
  if (descrizioneBene) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/beneCollettivo/descrizioneOggetto/${descrizioneBene}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getListaSearchCollettiva(res.data));
    }
  }
  return 0;
};

/**
 * Chiamata per ricerca bene collettivo tramite codice IMO, tramite il metodo getListaSearchCollettiva viene popolato il nodo listaSearchCollettiva
 * @function fetchSearchCollettivaImo
 * @param {String} imo Codice IMO
 * @returns getListaSearchCollettiva
*/
export const fetchSearchCollettivaImo = (imo) => async (dispatch) => {
  if (imo) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/beneCollettivo/codiceImo/${imo}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getListaSearchCollettiva(res.data));
    }
  }
  return 0;
};

/**
 * CSP-3098 - Ricerca adesioni per polizze collettive OverX (POST /overx/adesioni/search).
 * La response e' una pagina Spring { content: [Adesione...], totalElements, ... }: si salva l'intera
 * pagina (content + totalElements) nel nodo listaSearchAdesioni per abilitare il paging server-side a FE.
 * @function fetchSearchAdesioniCollettiva
 * @param {Object} request AdesioneSearchRequest (numeroPolizza, numeroAdesione, codiceFiscale, partitaIva, nome, cognome, *Contraente, ...)
 * @param {Number} page numero pagina (default 0)
 * @param {Number} size dimensione pagina (default 20)
 * @returns getListaSearchAdesioni popola la tabella adesioni
*/
export const fetchSearchAdesioniCollettiva = (request, page = 0, size = 20) => async (dispatch) => {
  // pulizia degli altri risultati di ricerca per non mischiare le modalita
  dispatch(getListaSearchPolizza([]));
  dispatch(getListaSearchPolizzaCollettiva([]));
  dispatch(getListaSearchCollettiva([]));
  const API_URI_CALL = `${API_URL}/api/v1/bff/overx/adesioni/search?page=${page}&size=${size}`;
  const fetchData = async () => axiosInstance.post(API_URI_CALL, request || {});

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('err', err);
  const content = Array.isArray(res?.data?.content) ? res.data.content : [];
  const totalElements = Number.isFinite(res?.data?.totalElements) ? res.data.totalElements : content.length;
  return dispatch(getListaSearchAdesioni({ content, totalElements }));
};

/**
 * Get Lista Pagamenti, tramite il metodo getListaPagamentiIstruttoria viene popolato il nodo listaPagamenti, popola tabella Pagamenti Istruttoria
 * @function fetchListaPagamenti
 * @param {String} sinistroId ID Sinstro
 * @returns getListaPagamentiIstruttoria popola tabella Pagamenti Istruttoria
*/
export const fetchListaPagamenti = (sinistroId) => async (dispatch) => {
  if (sinistroId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/${sinistroId}?limit=100`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getListaPagamentiIstruttoria(res.data));
    }
    return 0;
  }
  return null;
};

/**
 * Get Lista Garanzie pagamenti Indennizzo, tramite il metodo getGaranziePagamentoIndennizzo viene popolato il nodo garanziePagamentoIndennizzo, dopo aver selezionato un danneggiato nella quitenza viene popolata la tabella delle garanzie per Danneggiato.
 * @function fetchListaGaranziePagamentoIndennizzo
 * @param {String} danneggiatoId ID Danneggiato
 * @param {String} pagamentoId IDPagamento
 * @returns getGaranziePagamentoIndennizzo lista tabella delle garanzie per Danneggiato
*/
export const fetchListaGaranziePagamentoIndennizzo = (danneggiatoId, pagamentoId) => async (dispatch) => {
  if (danneggiatoId && pagamentoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/garanzie/${danneggiatoId}/${pagamentoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getGaranziePagamentoIndennizzo(res.data));
    }
    return 0;
  }
  return null;
};

//{{baseUrlOsBff}}/denunce/lista/danneggiatiJmil/{{nDenuncia}}/{{codiceProdotto}}
/**
 * Lista Danneggiati JMil, tramite il metodo getListaDanneggiatiJmil viene mìpopolato il nodo listaDanneggiatiJmil
 * @function fetchDanneggiatiJmil
 * @param {String} nDenuncia Numero Denuncia
 * @param {String} codiceProdotto Codice Prodotto
 * @param {String} codiceLiquidatore Codice Liquidatore
 * @param {Boolean} isOverx Se è polizza OverX
 * @returns getListaDanneggiatiJmil
 */
export const fetchDanneggiatiJmil = (nDenuncia, codiceProdotto, codiceLiquidatore, isOverx, codIspettoratoJmil) => async (dispatch) => {
  if (nDenuncia && codiceProdotto && codiceLiquidatore && codIspettoratoJmil) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/lista/danneggiatiJmil/${nDenuncia}/${codiceProdotto}/${codiceLiquidatore}/${isOverx}/${codIspettoratoJmil}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getListaDanneggiatiJmil(res.data));
    }
    return 0;
  }
  return null;
};

/**
  Get Lista Beneficiari Indennizzo, tramite il metodo getListaBeneficiariInennizzo viene popolato il nodo listaBeneficiariIndennizzo, tabella dei fiduciari in Pagamenti Indennizzo
 * @function fetchListaBeneficiariIndennizzo
 * @param {String} pagamentoId ID Pagamento
 * @returns getListaBeneficiariInennizzo tabella dei fiduciari
*/
export const fetchListaBeneficiariIndennizzo = (pagamentoId) => async (dispatch) => {// todo manca chiamata
  if (pagamentoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/beneficiario-indennizzo/${pagamentoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getListaBeneficiariInennizzo(res.data));
    }
    return 0;
  }
  return null;
};

/**
 * Prefetch suggested dataUltimoPagamento for a pagamentoId and cache it in state.ticket.suggestedDates
 * TTL: 5 minutes to avoid repeated calls during the same session
 */
export const fetchSuggestedDataUltimoPagamento = (pagamentoId) => async (dispatch) => {
  if (pagamentoId) {

    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/beneficiarioIndennizzo/${pagamentoId}/dataUltimoPagamento/suggerita`;
    console.debug('fetchSuggestedDataUltimoPagamento - calling', API_URI_CALL);
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);

    console.debug('fetchSuggestedDataUltimoPagamento - response', { res, err });
    if (err || !res || !res.data) {
      // keep UI fallback; do not throw
      return null;
    }

    // Normalize backend response: some BFF versions return { dataSuggerita: '...' },
    // others { dataUltimoPagamento: '...' }. Provide both keys so consumers
    // can safely read either `dataSuggerita` or `dataUltimoPagamento`.
    const normalized = {
      ...res.data,
      // prefer explicit dataUltimoPagamento, fall back to dataSuggerita
      dataUltimoPagamento: res.data?.dataUltimoPagamento ?? res.data?.dataSuggerita ?? null,
      fetchedAt: Date.now(),
    };
    dispatch(getSuggestedDate({ pagamentoId, payload: normalized }));
    return normalized;
    }
  return null;
};

/**
 * Lookup soggetto by codice fiscale (used in payment modal create mode).
 * API criteria:
 * - tipoSoggetto=PERSONA_FISICA
 * - codiceFiscale={codiceFiscale}
 *
 * @param {string} codiceFiscale
 * @param {string} tipoSoggetto (PERSONA_FISICA, PERSONA_GIURIDICA)
 * @returns {Promise<any|null>} backend response payload (raw)
 */
export const fetchSoggettoByCodiceFiscale = (codiceFiscale, tipoSoggetto) => async () => {
  if (!codiceFiscale) return null;

  const tipoSoggettoResolved =
    tipoSoggetto === 'PERSONA_GIURIDICA' ? 'PERSONA_GIURIDICA' : 'PERSONA_FISICA';
  // NOTE: endpoint path is assumed under "pagamenti" bff domain.
  // If your backend uses a different path, adjust API_URI_CALL accordingly.
  const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/beneficiario/${tipoSoggettoResolved}/${encodeURIComponent(
    codiceFiscale.toUpperCase(),
  )}`;

  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);

  if (res?.data) {
    return res.data;
  }

  console.log('fetchSoggettoByCodiceFiscale err', err);
  return null;
};

/**
 * Get Informazioni Utente, tramite il metodo getDatiUtente viene popolato il nodo userData
 * @function fetchUserData
 * @param {String} userId ID User
 * @returns getDatiUtente Info Utente
 */
export const fetchUserData = (userId) => async (dispatch) => {// todo manca chiamata
  if (userId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/utenti/${userId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      localStorage.setItem("userData", JSON.stringify(res.data)); //this is shit
      return dispatch(getDatiUtente(res.data));
    }
    return 0;
  }
  return null;
};

/**
 * Cambia stato quando viene effettuato un esito di apertura sinistro o chusura sinistro
 * @function fetchSinistroStatus
 * @param {String} nDenuncia numero Denuncia
 * @param {String} value Sinistro Aperto/Chiuso 
*/
export const fetchSinistroStatus = (nDenuncia, value) => async () => {
  if (nDenuncia && value) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/sinistro/stato/${nDenuncia}?stato=${value}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * Cambia stato quando viene effettuato un esito di apertura sinistro o chusura sinistro
 * @function fetchSinistroStatus
 * @param {String} nDenuncia numero Denuncia
 * @param {String} value Sinistro Aperto/Chiuso 
*/
export const fetchSinistroStatusMotivazione = (nDenuncia, value, motivazione, codiceMotivo) => async () => {
  if (nDenuncia && value) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/sinistro/stato/${nDenuncia}?stato=${value}`;
    // CSP-3170: quando presente, propaga anche il codice motivo selezionato dalla DDL.
    // CSP-2423: the body can also be the full reason object (code, text, damage in deductible).
    let body = { motivazioneSenzaSeguito: motivazione };
    if (motivazione && typeof motivazione === 'object') {
      body = motivazione;
    } else if (codiceMotivo) {
      body = { motivazioneSenzaSeguito: motivazione, codiceMotivoSenzaSeguito: codiceMotivo };
    }
    const fetchData = async () => axiosInstance.put(API_URI_CALL, body);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * Deductible available per garanzia and damage entirely in deductible recorded on the claim (CSP-2423)
 * @function fetchDannoFranchigia
 * @param {String} sinistroId UUID of the claim
 * @returns {Promise<{proposta: Array, registrato: Array}>} proposal rows and recorded damages, empty on error
*/
export const fetchDannoFranchigia = (sinistroId) => async () => {
  const vuoto = { proposta: [], registrato: [] };
  if (!sinistroId) return vuoto;
  const fetchData = async () => axiosInstance.get(`${API_URL}/api/v1/bff/denunce/sinistro/danno-franchigia/${sinistroId}`);
  const [res, err] = await executor.executeAsync(fetchData);
  if (err) {
    console.log('fetchDannoFranchigia error', err);
    return vuoto;
  }
  return {
    proposta: Array.isArray(res?.data?.proposta) ? res.data.proposta : [],
    registrato: Array.isArray(res?.data?.registrato) ? res.data.registrato : [],
  };
};

/**
 * in base al nomeEsitazione.toLowerCase() === "pagamento annullato" || nomeEsitazione.toLowerCase() === "quietanza non accettata" viene fatta una chiamata per settare lo staoo del pagamento OK o KO
 * @function fetchPagamentoStatus
 * @param {String} pagamentoId ID Pagamento
 * @param {String} value OK o KO
 * @param {String} tipoPagamento Tipo pagamento
*/
export const fetchPagamentoStatus = (pagamentoId, value, tipoPagamento) => async () => {
  if (pagamentoId && value) {
    let API_URI_CALL = "";
    if (tipoPagamento === "PAGAMENTO_SPESA")
      API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/pagamentoSpesa/statoPagamento/${pagamentoId}`;
    else
      API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/pagamentoIndennizzo/statoPagamento/${pagamentoId}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { statoPagamento: value });

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * Registra una nota di credito su un pagamento spese su fattura.
 * Mappa i campi raccolti dal form (nomi FE) sui nomi del contratto verso il BFF.
 * @function fetchNotaCredito
 * @param {String} pagamentoId ID pagamento spese
 * @param {Object} payload { documentoRiferimentoId, tipoNotaCredito, importoNotaCredito, numeroNotaCredito, dataNotaCredito }
 */
export const fetchNotaCredito = (pagamentoId, payload) => async () => {
  if (pagamentoId && payload) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/pagamentoSpesa/notaCredito/${pagamentoId}`;
    const body = {
      documentoRiferimentoId: payload.documentoRiferimentoId,
      tipoStorno: payload.tipoNotaCredito,
      importoStornato: payload.tipoNotaCredito === 'PARZIALE' ? Number(payload.importoNotaCredito) : null,
      numero: payload.numeroNotaCredito,
      dataDocumento: payload.dataNotaCredito,
    };
    const fetchData = async () => axiosInstance.put(API_URI_CALL, body);
    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * elenco motivazioni annullamento incarico
 * @function fetchMotivazioniAnnullamentoIncarico
 */
export const fetchMotivazioniAnnullamentoIncarico = () => async (dispatch) => {
  const API_URI_CALL = `${API_URL}/api/v1/bff/motivazioniAnnullamentoIncarico`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);

  dispatch(setMotivazioniAnnullamentoIncaricoLoading(true));
  const [res, err] = await executor.executeAsync(fetchData);
  dispatch(setMotivazioniAnnullamentoIncaricoLoading(false));

  if (res?.data) {
    dispatch(setMotivazioniAnnullamentoIncarico(res.data));
  }

  console.log('res', res);
  console.log('err', err);

  return err || res;
};

/**
 * crea motivazione annullamento incarico
 * @function createMotivazioneAnnullamentoIncarico
 * @param {String} descrizione motivazione
 */
export const createMotivazioneAnnullamentoIncarico = (descrizione) => async () => {
  if (!descrizione) return null;
  const API_URI_CALL = `${API_URL}/api/v1/bff/motivazioniAnnullamentoIncarico`;
  const fetchData = async () => axiosInstance.post(API_URI_CALL, { descrizione });

  const [res, err] = await executor.executeAsync(fetchData);

  console.log('res', res);
  console.log('err', err);

  return err || res;
};

/**
 * annulla incarico con motivazione (opzionale)
 * @function fetchAnnullaIncarico
 * @param {String} incaricoId ID incarico
 * @param {String} motivazioneAnnullamentoId ID motivazione annullamento (opzionale)
 */
export const fetchAnnullaIncarico = (incaricoId, motivazioneAnnullamentoId) => async () => {
  if (incaricoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/annulla/${incaricoId}`;
    const fetchData = async () =>
      axiosInstance.put(API_URI_CALL, { motivazioneAnnullamentoId: motivazioneAnnullamentoId || null });

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);

    return err || res;
  }
  return null;
};

/**
 * quando viene effettuato un esito di fiduciario nominato settiamo sull'incaricato lo stato di nominato.
 * Il cambio stato parte con l'esito gia' salvato su cContact: se fallisce l'incarico resta nello stato
 * precedente, quindi l'errore va mostrato all'operatore e non solo loggato in console (OSSQ-2403).
 * @function fetchIncaricatoStatus
 * @param {String} id ID incaricato
 * @param {String} value APERTO/EVASO/PERIZIA_NEGATIVA/PAGATO/RIFIUTATO/ANNULLATO/RIATTIVATO
 * @param {String} taskId task su cui mostrare il toast di errore
*/
export const fetchIncaricatoStatus = (id, value, taskId) => async (dispatch) => {
  if (id) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/incarichi/stato/${id}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { statoIncarico: value });

    const [, err] = await executor.executeAsync(fetchData);

    if (err) {
      console.error('fetchIncaricatoStatus error', err);
      axiosInstance.Cattura(err, taskId, "Stato incarico", "fetchToEvail", dispatch);
    }
  }
  return 0;
};

/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchCausaStatus
 * @param {String} id ID incaricato
 * @param {String} value CHIUSA/APERTA 
 */
export const fetchCausaStatus = (id, value) => async () => {
  if (id) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/stato/${id}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { stato: value });

    const [res, err] = await executor.executeAsync(fetchData);

    if (err) {
      throw err;
    }

    return res;
  }
  return null;
};

/**
 * Cambia stato quando viene effettuato un esito di apertura sinistro o chusura sinistro
 * @function fetchRecuperostatus
 * @param {String} recuperoId numero recuperoId
 * @param {String} value Sinistro Aperto/Chiuso 
*/
export const fetchRecuperoStatus = (recuperoId, value) => async () => {
  if (recuperoId && value) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recupero/stato/${recuperoId}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { stato: value });

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

const emulateWsMessage = (taskId, dispatch, err) => {
  //simuliamo la ws
  const uuid = crypto.randomUUID();
  const itemMessageToast = {
    requestId: uuid,
    entityName: "Parte coinvolta",
    taskId,
    fetchToEval: err.error,
    idTaskPadre: taskId,
    navigate: false,
    navigate2father: false /*|| !(queueDesc?.includes('MN - SHELL CLAIM')),*/,
  }; //genera l'eval di un fetch
  dispatch(setTotalItemMessageToast(itemMessageToast));
  const itemOk = `{"requestId":"${uuid}", "operation":{"status":"${err.message}", "statusCode": "${err.status}"}}`;
  const itemOkObj = { data: itemOk };
  //simuliamo il dispatch della ws
  dispatch(checkTotalItemMessageToast(itemOkObj));
};

/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchDanneggiatoStatus
 * @param {String} id ID incaricato
*/
export const fetchDanneggiatoStatus = (id, value, idSinistro, taskId) => async (dispatch) => {
  if (id) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/danneggiato/chiudi/${id}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { statoDanneggiato: value });

    const [res, err] = await executor.executeAsync(fetchData);

    // Check if executor returned error string (executor catches exceptions)
    if (res === "Execute async error" && err) {
      // Extract error from axios error structure
      const errorObj = {
        status: err.response?.status || err.status,
        message: err.response?.data?.message || err.message || 'Error',
        error: err.response?.data?.error || err.error || err.response?.data || err
      };

      if (errorObj.status === 9422) {
        emulateWsMessage(taskId, dispatch, errorObj);
        console.log('entrato err')
        return { success: false, error: errorObj };
      }
      if (errorObj) {
        console.log('others err')
        return { success: false, error: errorObj };
      }
    }

    if (err) {
      // Extract error from axios error structure
      const errorObj = {
        status: err.response?.status || err.status,
        message: err.response?.data?.message || err.message || 'Error',
        error: err.response?.data?.error || err.error || err.response?.data || err
      };

      if (errorObj.status === 9422) {
        emulateWsMessage(taskId, dispatch, errorObj);
        console.log('entrato err')
        return { success: false, error: errorObj };
      }
      console.log('others err')
      return { success: false, error: errorObj };
    }

    // Check for HTTP error status codes in response (axios might return response with error status)
    if (res && res.status && res.status >= 400) {
      const errorObj = {
        status: res.status,
        message: res.data?.message || res.statusText || 'Error',
        error: res.data?.error || res.data || res
      };

      if (errorObj.status === 9422) {
        emulateWsMessage(taskId, dispatch, errorObj);
        console.log('entrato err')
        return { success: false, error: errorObj };
      }
      console.log('others err')
      return { success: false, error: errorObj };
    }

    // Success case - only return success if there's no error
    if (res && (!res.status || res.status < 400)) {
      dispatch(fetchListaDanneggiati(idSinistro))
      return { success: true, error: null };
    }
  }
  return { success: false, error: null };
};

/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchRiservaNettaLorda
 * @param {String} id TaskId
 * @param {String} value valore LORDO/NETTO
*/
export const fetchRiservaNettaLorda = (id, value) => async (dispatch) => {
  if (id && value) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/update/gestione/${id}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, { gestioneSinistro: value });

    const [res, err] = await executor.executeAsync(fetchData);
    if (res) {
      dispatch(setRiservaNettaLorda(value))
    }
    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};
/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchRiservaNettaLorda
 * @param {String} id TaskId
 * @param {String} value valore LORDO/NETTO
*/
export const fetchRecuperoFranchigiaIstruttoria = (sinistroId) => async (dispatch) => {
  if (sinistroId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recupero/lista/${sinistroId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);
    if (res) {
      dispatch(setRecuperoFranchigiaIstruttoria(res.data))
    }
    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * quando viene effettuata la chiamata di aggiunta del beneficiario nel pagamento indennizzo viene triggerata questa chiamata che deve far comparire il componente di checkworld
 * @function fetchCheckWorld
*/
export const fetchCheckWorld = (objPostCheckWorld) => async (dispatch) => {
  const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/check-sanzioni`;
  const fetchData = async () => axiosInstance.post(API_URI_CALL, objPostCheckWorld);

  const [res, err] = await executor.executeAsync(fetchData);

  // CSP-3162: il controllo WorldCheck viene tracciato sul diario di trattazione lato backend,
  // quindi il diario va ricaricato anche quando la chiamata fallisce o l'esito è negativo
  if (objPostCheckWorld?.taskID) {
    dispatch(fetchNote(objPostCheckWorld.taskID));
  }

  if (err) {
    return console.error('Check World fetch error:', err);
  }

  if (res.data.status) {
    const formattedPayload = {
      checked: res.data.status,
      gridValues: res.data.grid || [],
      status: 'STANDBY',
      dataEsecuzione: res.data.dataEsecuzione
    };
    return dispatch(setCheckWorldStatus(formattedPayload));
  }

  return 0;
};

/**
 * Get Informazioni Utente, tramite il metodo getDatiUtente viene popolato il nodo userData
 * @function fetchRicercaDuplicato
 * @param {String} text testo da cercare
 * @param {String} type tipo:id/polizza/sinistro
 * @returns setListDuplicati lista elementi della ricerca
 */
export const fetchRicercaDuplicato = (text, type) => async (dispatch) => {// todo manca chiamata
  let API_URI_CALL;
  if (text.trim().length > 0) {
    switch (type) {
      case "id":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistriAndDenunce/idTask/${text}`;
        break;
      case "polizza":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistriAndDenunce/numeroPolizza/${text}`;
        break;
      case "sinistro":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistri/sinistroId?idPerSinistro=${text}`;
        break;
      case "contraente":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistro/contraente/${text}`;
        break;
      case "codiceDelegataria":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistriAndDenunce/codiceDelegataria/${text}`;
        break;
      case "numeroSinistroExternal":
        API_URI_CALL = `${API_URL}/api/v1/bff/denunce/cercaSinistriAndDenunce/numeroSinistroExternal/${text}`;
        break;
      default:
        break;
    }
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data && res.data.length > 0) {
      return dispatch(setListDuplicati(res.data));
    }
    if (res.data) {
      return dispatch(setListDuplicati([res.data]));
    }
    return dispatch(setListDuplicati([]));
    // return dispatch(setListDuplicati([
    //   { id: "10", numeroPolizza: "20", numeroSinistro: "30", esito: "test" },
    //   { id: "100", numeroPolizza: "200", numeroSinistro: "300", esito: "test2" }
    // ]));

  }
  return dispatch(setListDuplicati([]));
};

/**
 * Get Informazioni Utente, tramite il metodo getDatiUtente viene popolato il nodo userData
 * @function fetchDuplicatoSalva
 * @param {String} id ID del task
 * @returns getDatiUtente Info Utente
 */
export const fetchDuplicatoSalva = (duplicatoId, emailId, paramsFetchPostEsita, taskId, taskIdInCharge) => async (dispatch) => {// todo manca chiamata
  if (duplicatoId) {
    const payload = {
      "idEmail": emailId,
      "codicePraticaDaAssociare": duplicatoId,
      "taskIdAttuale": taskId,
      "esitaTask": paramsFetchPostEsita
    }
    const API_URI_CALL = `${API_URL}/api/v1/bff/email/riconciliazioneTask`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, payload);
    dispatch(toggleIsSlowFormVisible(true));
    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.status === 200) {
      //--> gestione toast
      const uuID = crypto.randomUUID();  //simuliamo una requestId
      const itemMessageToast = {
        "requestId": uuID,
        "entityName": "Esitazione task",
        "taskId": duplicatoId,
        "fetchToEval": "Esitazione",
        "idTaskPadre": "",
        "navigate": true,
        "navigate2father": false,
        "loader": ""
      } //genera l'eval di un fetch

      dispatch(setTotalItemMessageToast(itemMessageToast));
      //Simuliamo la risposta
      const itemOk = `{"requestId":"${uuID}", "operation":{"status":"SUCCESS", "statusCode": "7200"}}`
      const itemOkObj = { "data": itemOk }
      //simuliamo il dispatch della ws
      dispatch(checkTotalItemMessageToast(itemOkObj));

      // console.log('Res on Esiti ', response.data);
      // --> gestione NavTab
      dispatch(removeTaskToNav({ taskId }));
      dispatch(removeIdTicketInCharge(taskId)); //free frontend
      if (taskIdInCharge === `${taskId};`) {
        //se esistono altri ticket in modalità di modifica occorre lasciare invariato lo stato del customizer
        //diversamente andiamo in toggle
        dispatch(EnabledDisabledStateTrueFalsePresaCarico());
      }
      dispatch(toggleIsSlowFormVisible(false));
      dispatch(removeOpenedTicket({ taskId })); //TicketReducer
    }
    return 0;

  }
  return null;
};

/**
 * Il server ha rifiutato una scrittura perche' il task aperto non e' piu' in carico
 * all'utente (403 TASK_NON_IN_CARICO): lo si toglie dai task in carico, cosi' il
 * dettaglio torna in lettura e ricompare "Prendi in carico" [CSP-3808].
 * Se non resta nessun task in carico, il customizer torna in lettura: senza, la
 * presa in carico successiva lo invertirebbe di nuovo lasciando il task in lettura.
 * Limite: il 403 non dice quale task ha rifiutato la scrittura, quindi si toglie il
 * task aperto nel dettaglio, da cui partono le scritture.
 * Il messaggio all'utente parte da qui e non dai chiamanti, perche' non tutte le
 * scritture passano da Cattura (che salta il rifiuto gia' notificato). Un solo toast
 * per operazione: se ce n'e' gia' uno aperto per il task, non se ne accoda un altro.
 */
export const rilasciaTaskNonInCarico = () => (dispatch, getState) => {
  const { taskId, taskIdInCharge } = getState().ticketReducer;
  if (taskId && taskIdInCharge.split(';').includes(String(taskId))) {
    dispatch(removeIdTicketInCharge(taskId));
    if (getState().ticketReducer.taskIdInCharge === '') {
      dispatch(DisabledStateTrueFalsePresaCarico());
    }
  }
  const toastGiaAperto = getState().wsReducer.itemMessagesToast
    .some((toast) => toast.taskId === taskId && toast.localErr?.codice === CODICE_TASK_NON_IN_CARICO);
  if (!toastGiaAperto) {
    axiosInstance.Cattura({ codice: CODICE_TASK_NON_IN_CARICO, message: MESSAGGIO_TASK_NON_IN_CARICO }, taskId, 'Task', null, dispatch);
  }
};

/**
 * Get per calcolo massimali denunce/garanzia/cumoli?dataEvento=2023-06-01T08:49:04.000Z&dataScadenza=2023-07-30T20:49:04.000Z&dataEmissione=2023-05-03T12:49:04.000Z&garanziaPrincipale=RCT - Responsabilità Civile verso Terzi&keyMassimale=massimalePersone&valoreMassimale=200000
 * @function fetchGetMassimali
 * @param {String} nDenuncia numero Denuncia
 * @param {String} value Sinistro Aperto/Chiuso 
*/
export const fetchGetMassimali = (dataEvento, dataScadenza, dataEmissione, garanziaPrincipale, keyMassimale, valoreMassimale, numeroPolizza) => async (dispatch) => {

  if (dataEvento && dataScadenza && dataEmissione && garanziaPrincipale && keyMassimale && valoreMassimale) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/garanzia/cumoli?dataEvento=${dataEvento}&dataScadenza=${dataScadenza}&dataEmissione=${dataEmissione}&garanziaPrincipale=${garanziaPrincipale}&keyMassimale=${keyMassimale}&valoreMassimale=${valoreMassimale}&numeroPolizza=${numeroPolizza}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
    if (res.data) {
      dispatch(getCalcoloMassimali(res.data));
    }

  }
};


/**
 * get Lista BeneficiariRecuperi, per popolare la lista nella modale dei recuperi
 * @function fetchListaBenRecuperoFranchigia
 * @param {String} idPagamento ID idPagamento
 * @returns lista beneficiari recupero franchigia
*/
export const fetchListaBenRecuperoFranchigia = (idPagamento) => async (dispatch) => {
  if (idPagamento) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/beneficiario-indennizzo/${idPagamento}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(setListaBeneficiariRecuperoFranchigia(res.data));
  }
  return null;
};

/**
 * get Lista BeneficiariRecuperi, per popolare la tabella dei recuperi
 * @function fetchListaBenRecuperoFranchigia
 * @param {String} idRecupero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const fetchListaBenRecuperoFranchigiaTable = (idRecupero) => async (dispatch) => {
  if (idRecupero) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recupero/lista/beneficiario-franchigia/${idRecupero}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(setListaBeneficiariRecuperoFranchigiaTable(res.data));
  }
  return null;
};

/**
 * get Lista fetchListaDebitoreRecuperoRivalsa, per popolare la tabella dei recuperi
 * @function fetchListaDebitoreRecuperoRivalsa
 * @param {String} idRecupero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const fetchListaDebitoreRecuperoRivalsa = (rivalsaId) => async (dispatch) => {
  if (rivalsaId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recuperoRivalsa/lista/debitore-rivalsa/${rivalsaId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(setListaDebitoreRecuperoRivalsa(res.data));
    }
  }
  return null;
};


/**
 * get Lista BeneficiariRecuperi, per popolare la tabella dei recuperi
 * @function fetchListaBenRecuperoFranchigia
 * @param {String} idRecuoero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const fetchListaAllegatiPagamentoIndennizzo = (idSinistro, tipologiaAllegato, tipologiaPagamento, taskId) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = tipologiaAllegato === 'QUIETANZA' ? `${API_URL}/api/v1/bff/pagamenti/lista/allegati/${idSinistro}/tipologiaAllegato/${tipologiaAllegato}` : `${API_URL}/api/v1/bff/pagamenti/lista/allegati/${idSinistro}/tipologiaAllegato/${tipologiaPagamento}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (err) {
      if (tipologiaAllegato === "QUIETANZA") {
        dispatch(getListaAllegatiQuietanza([]));
      }
      if (taskId) {
        axiosInstance.Cattura(err, taskId, "Allegati Quietanza", "fetchToEvail", dispatch);
      }
      return null;
    }

    const allegati = Array.isArray(res?.data) ? res.data : [];

    const byTipologia = (tipologia) => allegati.filter(item => item?.tipologia === tipologia);

    if (tipologiaAllegato === "QUIETANZA") {
      dispatch(getListaAllegatiQuietanza(byTipologia("Quietanza")));
    }
    if (tipologiaAllegato === "PagoPA") {
      dispatch(getListaAllegatiPagoPa(byTipologia("PagoPA")));
    }
    if (tipologiaPagamento === 'DOCUMENTI_LEGALI') {
      dispatch(getListaAllegatiInformativaPrivacy(allegati));
    }

    if (tipologiaPagamento === 'PAGAMENTO_SPESE' || tipologiaPagamento === 'PAGAMENTO_INDENNIZZO') {
      dispatch(getListaAllegatiPagamentoIndennizzo(allegati));
    }

    // Default dispatch if none of the above matched
  }
  return null;
};
/**
 * get Lista BeneficiariRecuperi, per popolare la tabella dei recuperi
 * @function fetchListaBenRecuperoFranchigia
 * @param {String} idRecuoero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const fetchAllegatoPagamentoIndennizzo = (pagamentoId) => async (dispatch) => {
  if (pagamentoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/allegati/pagamento/${pagamentoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    if (res) {
      return dispatch(getAllegatoPagamentoIndennizzo(res.data));
    }
    // Gestione 404: nessun allegato associato -> non mandare l'app in errore
    if (err && (err.status === 404 || err.status === '404')) {
      return dispatch(getAllegatoPagamentoIndennizzo([]));
    }
  }
  return null;
};
/**
 * get Lista BeneficiariRecuperi, per popolare la tabella dei recuperi
 * @function fetchListaBenRecuperoFranchigia
 * @param {String} idRecuoero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const fetchAllegatoPagamentoSpesa = (pagamentoId) => async (dispatch) => {
  if (pagamentoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/lista/allegati/pagamentoSpesa/${pagamentoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res) {
      return dispatch(getAllegatoPagamentoIndennizzo(res.data));
    }
    // Gestione 404: nessun allegato associato -> non mandare l'app in errore
    if (err && (err.status === 404 || err.status === '404')) {
      return dispatch(getAllegatoPagamentoIndennizzo([]));
    }
  }
  return null;
};

export const fetchSinistroIngente = (sinistroIngente, sinistroId) => async (dispatch) => {

  if (sinistroId) {
    const obj = {
      "sinistroIngente": sinistroIngente === "" ? null : sinistroIngente
    }
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/update/sinistroIngente/${sinistroId}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, obj);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
    if (err) {
      throw err;
    }
    if (res) {
      return dispatch(setSinistroIngente(sinistroIngente));
    }
  }
  return null;
};

export const fetchValidazioneSalvaNota = (idValidazione, nota) => async (dispatch) => {
  if (idValidazione && nota) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/validazioni/update/notaResponsabile/${idValidazione}?notaResponsabile=${nota}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
    if (res) {
      return dispatch(setValidazioneNotaResponsabile(nota));
    }
  }
  return null;
};

/**
 * Eseguo Put per aggiornare a Db Idbene quando cambio id dalla tabella beni assicurati
 * @function fetchSinistroStatus
 * @param {String} nDenuncia numero Denuncia
 * @param {String} idBene idBene Collettivo selezionato
 * @param {String} nPolizza numero Polizza
*/
export const fetchPutIdBeneCollettive = (nDenuncia, idBene, nPolizza) => async () => {
  if (nDenuncia && idBene && nPolizza) {
    const objPutInBene = {
      "numeroPolizza": nPolizza,
      "idBene": idBene
    }
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/update/idBene/${nDenuncia}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, objPutInBene);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('res', res);
    console.log('err', err);
  }
  return 0;
};

/**
 * Salva dati shellClaim 
 * @param {*String} taskId 
 * @param {*String} idSinistro 
 * @param {*Object} datiShellClaim 
 * @returns 
*/
export const fetchSaveDatiShellClaim = (taskId, idSinistro, datiShellClaim) => async (dispatch) => {
  if (taskId && idSinistro && datiShellClaim) {
    return axiosInstance.post(`${API_URL}/api/v1/bff/ShellClaim/create/${idSinistro}`, datiShellClaim)
      .then(() => {

      }).catch((e) => {
        axiosInstance.Cattura(e, taskId, "Dati Shell Claim Aggiornati", "fetchToEval", dispatch);
      });
  }
  return null;
}

export const fetchGetOperatori = (taskId, itemEsitazione, queueId) => async (dispatch) => {
  if (taskId && itemEsitazione > 0) {
    const API_URI_CALL_DAYS = `${API_URL}/api/v1/bff/task/operatori/${taskId}/${queueId}/${itemEsitazione}`
    const fetchDataCallDays = async () => axiosInstance.get(API_URI_CALL_DAYS);
    const [resUser, errUser] = await executor.executeAsync(fetchDataCallDays);
    console.log('errUser ', errUser);
    if (resUser) {
      return dispatch(setOperatori(resUser.data));
    }
  }
  return null;
}

const buildIbanValidationServiceError = (status) => ({
  validations: {
    err: {
      code: `${status ?? 'UNKNOWN'}`,
      message: "Problemi con il servizio di validazione Iban, riprovare",
      passedValidation: true
    }
  }
});

export const fetchGetValidazioneIban = (iban, setCollapsed, setIbanCheck, setMetodoPagamento, handleChildData, controllFormInput, setLoaderBtn, setShowAnnoMounth, typePagamento, setDisabledSepa) => async (dispatch) => {
  if (iban !== '' && iban !== " " && iban !== null && iban !== undefined) {
    setLoaderBtn(true)
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/check/iban/${iban}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    if (err?.status !== null && err?.status !== undefined && err?.status !== "") {
      const objErrors = buildIbanValidationServiceError(err.status);
      dispatch(getValidazioneIban(objErrors));
      setLoaderBtn(false)
      setIbanCheck(false)
      return setCollapsed(true);
    }
    const responseData = res?.data;
    if (!responseData || typeof responseData !== 'object') {
      const objErrors = buildIbanValidationServiceError(err?.status);
      dispatch(getValidazioneIban(objErrors));
      setLoaderBtn(false)
      setIbanCheck(false)
      return setCollapsed(true);
    }
    const staus = responseData?.sepa_data?.SCT
    const controllFormInputValid = controllFormInput || {}
    if (staus === "YES") {
      if (typePagamento) {
        setShowAnnoMounth(false)
      }
      if ((controllFormInputValid?.metodoDiPagamento || controllFormInputValid?.metodoPagamento) === "AD") {
        setShowAnnoMounth(true)
      }
    } else {
      setMetodoPagamento("BONIFICO_MANUALE")
      setShowAnnoMounth(false)
      if ((controllFormInputValid?.metodoDiPagamento || controllFormInputValid?.metodoPagamento) === "AD") {
        setShowAnnoMounth(true)
        setMetodoPagamento("AD")
      }
      if (typePagamento === EnumService.TIPOPAGAMENTO.LQIND) {
        setDisabledSepa(true)
      }
      if (controllFormInput !== undefined && controllFormInput !== null && typePagamento === EnumService.TIPOPAGAMENTO.LQIND) {
        if (handleChildData) {
          handleChildData({
            ...controllFormInputValid,
            "metodoDiPagamento": "BONIFICO_MANUALE",
          })
        }
      }
    }
    setLoaderBtn(false)
    dispatch(getValidazioneIban(responseData));
    setIbanCheck(Boolean(responseData?.validIban))
    return setCollapsed(true);
  }
  return null;
}

export const simplifyValidationIban = (iban, setCollapsed, setLoaderBtn, setIbanCheck, canale, codiceProdotto, importo, setIsUsedIban, metodoPagamento) => async (dispatch) => {
  const checkPaymentMethod = metodoPagamento !== 'INSTANT_PAYMENT'
  if (iban !== '' && iban !== " " && iban !== null && iban !== undefined) {
    setLoaderBtn(true)
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/check/iban/${iban}?canale=${canale}&prodotto=${codiceProdotto}&amount=${importo}&dontCheckIp=${checkPaymentMethod}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    if (err?.status !== null && err?.status !== undefined && err?.status !== "") {
      const objErrors = buildIbanValidationServiceError(err.status);
      dispatch(getValidazioneIban(objErrors));
      setLoaderBtn(false)
      setIbanCheck(false)
      setIsUsedIban(false)
      return setCollapsed(true);
    }
    const responseData = res?.data;
    if (!responseData || typeof responseData !== 'object') {
      const objErrors = buildIbanValidationServiceError(err?.status);
      dispatch(getValidazioneIban(objErrors));
      setLoaderBtn(false)
      setIbanCheck(false)
      setIsUsedIban(false)
      return setCollapsed(true);
    }

    setLoaderBtn(false)
    dispatch(getValidazioneIban(responseData));
    setIbanCheck(Boolean(responseData?.validIban))
    setIsUsedIban(Boolean(responseData?.isIbanUsed))
    return setCollapsed(true);
  }
  return null;
}

/** Placeholder inviato al BFF per i criteri geografici non valorizzati. */
const POI_EVENTO_PLACEHOLDER = '-';

const toPoiEventoPathParam = (value) => {
  const trimmed = value?.toString().trim();
  return trimmed ? encodeURIComponent(trimmed) : POI_EVENTO_PLACEHOLDER;
}

/**
 * Carica gli eventi catastrofali compatibili con il luogo evento del sinistro.
 * Basta la data evento più almeno un criterio geografico: il perimetro degli
 * eventi può essere censito anche per sola regione o solo CAP, e l'evento può
 * trovarsi in un luogo privo di comune o CAP (CSP-3196).
 * La data va come istante UTC con la Z (CSP-2788 T4, U17): la data evento del sinistro e' gia' in UTC.
 */
export const getListaCodiceEvento = (dataEvento, regioneEvento, provinciaEvento, capevento, comuneEvento) => async (dispatch) => {
  const hasCriterioGeografico = Boolean(regioneEvento || provinciaEvento || capevento || comuneEvento);
  const newDate = istanteUtc(dataEvento);
  if (newDate && hasCriterioGeografico) {
    dispatch(setListaCodiceEvento([]));
    const API_URI_CALL_DAYS = `${API_URL}/api/v1/bff/denunce/lista/eventiCatastrofali/${newDate}/${toPoiEventoPathParam(regioneEvento)}/${toPoiEventoPathParam(provinciaEvento)}/${toPoiEventoPathParam(comuneEvento)}/${toPoiEventoPathParam(capevento)}`
    const fetchGetCodiceEvento = async () => axiosInstance.get(API_URI_CALL_DAYS);
    const [resCodEv, errCodEv] = await executor.executeAsync(fetchGetCodiceEvento);
    console.log('errUser ', errCodEv);
    if (resCodEv) {
      return dispatch(setListaCodiceEvento(resCodEv.data));
    }
  }
  return dispatch(setListaCodiceEvento([]));
}

/**
 * get Lista Incassi,
 * vengono visualizzati Incassi tramite RecuperoId, 
 * per popolare la tabella degli incassi nella sezione recuperi
 * @function fetchListaIncassiRecuperi
 * @param {String} idRecupero ID idRecuoero
 * @returns lista incassi table recupero
*/
export const fetchListaIncassiRecuperi = (recuperoId) => async (dispatch) => {
  if (recuperoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recupero/lista/incassi/${recuperoId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);

    return dispatch(getAllIncassi(res.data));
  }
  return null;
};


/**
 * get Lista Incassi,
 * vengono visualizzati Incassi tramite RecuperoId, 
 * per popolare la tabella degli incassi nella sezione recuperi
 * @function fetchListaIncassiRecuperi
 * @param {String} idRecupero ID idRecuoero
 * @returns lista incassi table recupero
*/
export const fetchInstantPayment = (canale, prodotto, importo) => async (dispatch) => {
  if (canale && prodotto && importo) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/available/instant-payment?canale=${canale}&prodotto=${prodotto}&amount=${importo}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    console.log('err', err);
    if (res.data) {
      return dispatch(getAvailableInstantPayment(res.data));
    }
    if (err) {
      return dispatch(getAvailableInstantPayment({ disable: false }));
    }
  }
  return dispatch(getAvailableInstantPayment({ disable: false }));
};

/**
 * get Lista Incassi,
 * vengono visualizzati Incassi tramite RecuperoId, 
 * per popolare la tabella degli incassi nella sezione recuperi
 * @function putIncassiRecuperi
 * @param {String} idRecupero ID idRecuoero
 * @returns lista beneficiari table recupero franchigia
*/
export const putIncassiRecuperi = (recuperoId, importo, data, isChecked, idGaranzia) => async () => {
  if (recuperoId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recupero/update/incassi/${recuperoId}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, {
      importo,
      flagTotale: isChecked,
      idGaranzia: idGaranzia || null,
      dataIncasso: data
    });

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      return res
    }
    return err
  }
  return null;
};

/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchRiservaNettaLorda
 * @param {String} id TaskId
 * @param {String} value valore LORDO/NETTO
*/
export const fetchRecuperoGaranzieRivalsa = (rivalsaId) => async (dispatch) => {
  if (rivalsaId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recuperoRivalsa/lista/garanzie/${rivalsaId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res.data && res.data.garanziaList) {
      dispatch(setRecuperoGaranzieRivalsa(res.data.garanziaList));
      dispatch(setImportoTotaleGaranzieRivalsa(res.data.totaleImportoGaranzie));
    }
    //console.log('res',fetchData,res);
    console.log('err', err);
  }
  return 0;
};

/**
 * quando viene effettuato un esito su un task di causa con il valore dello stato aperta o chiusa
 * @function fetchListaIncassiRivalsa
 * @param {String} id TaskId
 * @param {String} value valore LORDO/NETTO
*/
export const fetchListaIncassiRivalsa = (rivalsaId) => async (dispatch) => {
  if (rivalsaId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/recuperoRivalsa/incassi/${rivalsaId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res.data) {
      dispatch(getAllIncassiRivalsa(res.data))
    }
    //console.log('res',fetchData,res);
    console.log('err', err);
  }
  return 0;
};

export const gestioneSanzioniDeroga = (esito, motivazione, taskId, autore, typePagamento) => async (dispatch) => {
  const objectGestioneSanzioniDeroga = { esito, motivazione, autore, taskId, tipoPagamento: typePagamento }
  console.log('objectGestioneSanzioniDeroga', objectGestioneSanzioniDeroga);
  if (esito && taskId) {
    return axiosInstance.put(`${API_URL}/api/v1/bff/pagamenti/update/noteSanzioni/esiti-pagamenti`, objectGestioneSanzioniDeroga)
      .then((response) => {
        console.log('Res on Promemoria ', response.data);
        //In caso di esito positivo, mette il messaggio in coda in attesa della risposta WS
        const itemMessageToast = {
          "requestId": response.data.requestId,
          "entityName": "Sanzioni",
          "taskId": taskId,
          "fetchToEval": "SanzioniConferma",
          "esito": esito
        }// genera l'eval di un fetch
        dispatch(setTotalItemMessageToast(itemMessageToast));
      }).catch((e) => {
        axiosInstance.Cattura(e, taskId, "Sanzioni", "fetchToEvail", dispatch)
      });
  }
  return null
};

/**
 * put Data Liquidazione Anticipata
 * @param {String} nDenuncia numero Denuncia,
 * @param {Date} dataLiquidazioneAnticipata data Liquidazione Anticipata
 * @function putDataLiquidazioneAnticipata
 * @returns {Promise} axios response 
 */
export const putDataLiquidazioneAnticipata = (nDenuncia, dataLiquidazioneAnticipata) => async () => {
  if (nDenuncia) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/dataLiquidazione/${nDenuncia}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, {
      dataLiquidazioneAnticipata: dataLiquidazioneAnticipata || ''
    });

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      return res
    }
    return err
  }
  return null;
};


/**
 * Update Dati Particolari per danneggiati
 * @function putDatiParticolari
 * @param {Object} payload
 * @param {String} sinistroId
 * @returns {Promise} axios response
 */
export const putDatiParticolari = (payload, sinistroId) => async () => {
  if (sinistroId) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/update/datiParticolari/${sinistroId}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, payload);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      return res
    }
    return err
  }
  return null;
}

/**
 * put StatoRichiestaAnticipo
 * @param {String} nDenuncia numero Denuncia,
 * @param {String} statoRichiestaAnticipo stato Richiesta Anticipo
 * @function putDataLiquidazioneAnticipata
 * @returns {Promise} axios response 
 */
export const putStatoRichiestaAnticipo = (nDenuncia, statoRichiestaAnticipo) => async () => {
  if (nDenuncia) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/statoRichiestaAnticipo/${nDenuncia}`;
    const fetchData = async () => axiosInstance.put(API_URI_CALL, {
      statoRichiestaAnticipo
    });

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      return res
    }
    return err
  }
  return null;
};

/**
 * WokFlow Run
 * @function postWorkFlowRun 
 * @param {*String} nSinistro 
 * @param {*Object} denominazioneUtente 
 * @returns 
*/
export const postWorkFlowRun = (nSinistro, denominazioneUtente) => async () => {
  const itemWorkFlow = {
    "user": denominazioneUtente,
    "claim_number": nSinistro
  }
  if (nSinistro && denominazioneUtente) {
    return axiosInstance.post(`${API_URL_WORKFLOW}`, itemWorkFlow)
      .then(() => {
        console.log('WorkFlow Run OK');
      }).catch((e) => {
        console.log(e)
      });
  }
  return null;
}

/**
 * Update docs Run
 * @function postDmsUpdate 
 * @param {*Object} itemsDocMetadata 
 * @returns 
*/
export const postDmsUpdate = (itemsDocMetadata) => async () => {
  //if(itemsDocMetadata.metadata.sinistro)
  //{
  return axiosInstance.post(`${API_URL_DMS}`, itemsDocMetadata)
    .then(() => {
      console.log('Update Docs Run OK');
    }).catch((e) => {
      console.log(e)
    });
  //}
  //return null;
}

export const getListaDanneggiatiSinistroCausa = (idSinistro) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/listaDanneggiati/${idSinistro}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      dispatch(listaDanneggiatiSinistroCausa(res.data))
      return res
    }
    return err
  }
  return null;
}

export const getDannegiatiCausa = (idCausa) => async (dispatch) => {
  if (idCausa) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/danneggiato/${idCausa}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      dispatch(danneggiatoSelezionatoCausa(res.data))
      return res
    }
    return err
  }
  return null;
}




export const getListaIncarichiSinistroCausa = (idSinistro, idCausa) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/listaIncarichi/${idSinistro}/${idCausa}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      dispatch(listaIncarichiSinistroCausa(res.data))
      return res
    }
    return err
  }
  return null;
}

export const getCausaValidazioneApertura = (idCausa) => async (dispatch) => {
  if (idCausa) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/validazione-apertura/${idCausa}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      dispatch(setCausaValidazioneApertura(res.data))
      return res
    }
    return err
  }
  return null;
}

export const putCausa = (type, id, idCausa) => async () => {
  if (!id) return null;

  const basePath =
    type === "danneggiato"
      ? `/api/v1/bff/cause/${id}`
      : `/api/v1/bff/cause/incarico/${id}`;

  const API_URI_CALL = `${API_URL}${basePath}`;

  const fetchData = async () =>
    axiosInstance.put(API_URI_CALL, { causaId: idCausa });

  const [res, err] = await executor.executeAsync(fetchData);

  return res || err;
};

export const getListaIncarichiCausa = (idCausa) => async (dispatch) => {
  if (idCausa) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/cause/incarichi/${idCausa}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);

    const [res, err] = await executor.executeAsync(fetchData);

    if (res) {
      dispatch(listaIncarichiSelezionatiCausa(res.data))
      return res
    }
    return err
  }
  return null;
}

export const fetchDiniego = (idSinistro) => async (dispatch) => {
  if (idSinistro) {
    const API_URI_CALL = `${API_URL}/api/v1/bff/denunce/diniego/${idSinistro}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    if (res) {
      dispatch(getDiniego(res.data))
      return res
    }
    return err
  }
  return null
}

export const fetchAttachmentsEmail = (taskId) => async (dispatch) => {
  if (!taskId) {
    dispatch(getAttachmentsEmail([]));
    return null;
  }
  try {
    const API_URI_CALL = `${API_URL}/api/v1/bff/email/allegati/${taskId}`;
    const fetchData = async () => axiosInstance.get(API_URI_CALL);
    const [res, err] = await executor.executeAsync(fetchData);
    if (err || !res || typeof res !== 'object') {
      console.error('fetchAttachmentsEmail error', err);
      dispatch(getAttachmentsEmail([]));
      return null;
    }
    dispatch(getAttachmentsEmail(Array.isArray(res.data) ? res.data : []));
    return res;
  } catch (e) {
    console.error('fetchAttachmentsEmail failed', e);
    dispatch(getAttachmentsEmail([]));
    return null;
  }
}

export const fetchSepaCountries = () => async (dispatch) => {
  const API_URI_CALL = `${API_URL}/api/v1/bff/pagamenti/listaPaesiSepa`;
  const fetchData = async () => axiosInstance.get(API_URI_CALL);
  const [res, err] = await executor.executeAsync(fetchData);
  if (res) {
    dispatch(getSepaCountries(res.data))
    return res
  }
  return err
}

/**
 * Centralizza il save incarico manuale, mantenendo nel slice la decisione del ramo
 * legacy vs Portal MSA e lasciando al componente solo la raccolta dati UI.
 *
 * @param {Object} payload input del save incarico.
 * @param {string} payload.idIncarico identificativo incarico.
 * @param {Object} payload.paramsDatiIncarico payload legacy per il save locale.
 * @param {boolean} payload.isPortalManualSync true se il fiduciario selezionato arriva dal ramo Portal.
 * @param {Object|null} payload.portalManualSyncPayload payload del nuovo endpoint Portal.
 * @returns {Promise<import('axios').AxiosResponse|*>} response axios o errore normalizzato.
 */
export const saveDatiIncarico = ({
  idIncarico,
  paramsDatiIncarico,
  isPortalManualSync = false,
  portalManualSyncPayload = null,
}) => async () => {
  if (!idIncarico) {
    return null;
  }

  /**
   * Il FE passa al BFF solo i campi minimi Portal-specifici:
   * il BFF completa i default di integrazione e governa il rollback applicativo.
   */
  const API_URI_CALL = isPortalManualSync
    ? `${API_URL}/api/v1/bff/incarichi/${idIncarico}/portal/manual-sync`
    : `${API_URL}/api/v1/bff/incarichi/save/${idIncarico}`;

  const fetchData = async () => (isPortalManualSync
    ? axiosInstance.post(API_URI_CALL, portalManualSyncPayload)
    : axiosInstance.put(API_URI_CALL, paramsDatiIncarico));

  const [res, err] = await executor.executeAsync(fetchData);
  if (err) {
    throw err;
  }
  return res;
};

// ============================================================
// CSP-3136 - Clausolario quietanza globale (thunks pass-through BFF)
// ============================================================

/**
 * GET /clausolario?lingua=...
 * @param {string} [lingua] ISO3 (ITA/ENG/ESP). Se omesso restituisce tutte le lingue.
 * @returns {Promise<Array>} lista voci clausolario
 */
export const fetchClausolarioQuietanza = async (lingua) => {
  const url = lingua
    ? `${API_URL}/api/v1/bff/clausolario?lingua=${encodeURIComponent(lingua)}`
    : `${API_URL}/api/v1/bff/clausolario`;
  const [res, err] = await executor.executeAsync(() => axiosInstance.get(url));
  if (err) {
    throw err;
  }
  return res?.data || [];
};

/**
 * POST /clausolario
 * @param {{codice?:string, titolo?:string, contenuto:string, lingua:string}} dto
 * @returns {Promise<Object>} voce creata
 */
export const createClausolarioQuietanza = async (dto) => {
  const url = `${API_URL}/api/v1/bff/clausolario`;
  const [res, err] = await executor.executeAsync(() => axiosInstance.post(url, dto));
  if (err) {
    throw err;
  }
  return res?.data;
};

/**
 * DELETE /clausolario/{id}
 * @param {string} id UUID
 */
export const deleteClausolarioQuietanza = async (id) => {
  const url = `${API_URL}/api/v1/bff/clausolario/${id}`;
  const [, err] = await executor.executeAsync(() => axiosInstance.delete(url));
  if (err) {
    throw err;
  }
};



export default TicketSlice.reducer;


